/**
 * Map orchestrator journey loanReview → compact admin dashboard view model.
 * Never surface raw agentDetails blobs as the primary UI.
 */

export type LoanReviewOutcomeLabel =
  | 'Approved'
  | 'Needs review'
  | 'Not eligible'
  | 'Pending'
  | 'Failed'
  | 'None';

/** Score-driven lane → hero outcome label. */
const LANE_OUTCOME_LABEL: Record<string, LoanReviewOutcomeLabel> = {
  APPROVE: 'Approved',
  REVIEW: 'Needs review',
  REJECT: 'Not eligible',
  FOLLOW_PROVENIR: 'Not eligible',
  FOLLOW_CREDIT_DECISION: 'Not eligible',
};

export type PipelineStageStatus = 'done' | 'soft' | 'blocked' | 'missing' | 'skipped';

export interface LoanReviewGateSubCheck {
  name: string;
  label: string;
  status: string;
  reasonCodes: string[];
}

export interface LoanReviewGateRow {
  gateId: string;
  title: string;
  stage: string;
  status: string;
  reasonCodes: string[];
  blocksStp: boolean;
  subChecks: LoanReviewGateSubCheck[];
}

export interface LoanReviewCrossCheckRow {
  matchType: string;
  compare: string;
  /** MiTek / OTP / ID side when applicable */
  identity: string | null;
  /** Bank / income side when applicable */
  income: string | null;
  /** Verified employer when applicable */
  employer: string | null;
  /** Bureau / TransUnion side when applicable */
  bureau: string | null;
  /**
   * The columns this comparison actually involves. A column in here with no value
   * means the source was asked and returned nothing — which the table shows as
   * "Unavailable". A column absent from here is simply not part of this check, and
   * stays a dash. Without this, both cases are `null` and read identically.
   */
  asked?: CrossCheckColumn[];
  status: string;
  reasonCodes: string[];
}

export interface LoanReviewIdentityDedup {
  status: string;
  reasonCodes: string[];
}

export interface LoanReviewPipelineStage {
  id: 'gates' | 'agent1' | 'agent2' | 'agent3' | 'agent4';
  label: string;
  status: PipelineStageStatus;
  subtitle: string;
}

/** Agent 3: one qualifying external finding, with the proof behind it. */
export interface LoanReviewExternalFinding {
  code: string;
  /** HIGH | MEDIUM | LOW — how much this finding cost the domain score. */
  impact: string;
  /** Points deducted from the domain's 100. */
  deduction: number | null;
  /** When the event happened. A finding without one is never reported. */
  eventDate: string | null;
  /** Publishing organisation, e.g. 'CBC News'. */
  publisher: string | null;
  sourceDomain: string | null;
  sourceUrl: string | null;
  /** OFFICIAL | REPUTABLE_MEDIA | USER_GENERATED | UNKNOWN — audit only. */
  sourceTier: string | null;
  /** 0-1 that this is THIS employer and not a same-named company elsewhere. */
  employerMatchConfidence: number | null;
  evidenceQuote: string | null;
}

/**
 * Agent 3's domain subscore and whether it counted.
 *
 * `gateStatus` separates three states that otherwise look identical to a reviewer:
 * `UNAVAILABLE` (we never looked), `PASS` with no findings (looked, clean), and
 * `REVIEW` with findings.
 */
export interface LoanReviewExternalSubscore {
  /** 0-100. Null when the gate was UNAVAILABLE — nothing was scored. */
  score: number | null;
  /** Adjuster weight. 0 means computed and shown but not applied. */
  weight: number | null;
  /** Points this moved the final score by. Negative on adverse findings. */
  adjustment: number | null;
  /** Legacy alias for `adjustment`, kept so older payloads still render. */
  contribution: number | null;
  /** Agent 1's score, the number the adjustment was applied to. */
  agent1Score: number | null;
  /** weight_zero | gate_absent | gate_unavailable — why it did not count. */
  notCountedReason: string | null;
  counted: boolean;
  compositeScore: number | null;
  gateStatus: string | null;
  /** Findings considered and rejected — proof we actually looked. */
  discardedCount: number;
  researchedAt: string | null;
  /** 100 − Σ deductions, before the floor. Equals `score` unless the floor bit. */
  rawScore: number | null;
  /** Lowest a subscore may fall. Without it the shown score looks like bad maths. */
  floor: number | null;
  totalDeduction: number | null;
}

export interface LoanReviewAgentSlice {
  present: boolean;
  lines: Array<{ label: string; value: string }>;
  /** Short operator blurb above the metric grid (e.g. Agent 2). */
  blurb?: string | null;
  summary?: string | null;
  /** Agent 3: plain-language Canada macro narrative */
  macroSummary?: string | null;
  /** Agent 3: sector + employer narrative */
  sectorSummary?: string | null;
  concerns?: string[];
  flags?: string[];
  /** Agent 3: qualifying findings, highest impact first. */
  externalFindings?: LoanReviewExternalFinding[];
  /** Agent 3: the domain subscore and whether it reached the composite. */
  externalSubscore?: LoanReviewExternalSubscore | null;
  /**
   * Agent 2: adverse findings on this customer's own record, worst first.
   *
   * `deduction` is what was actually charged; `baseDeduction` is the price before
   * the recency discount, and `recencyMultiplier` is the discount itself. All
   * three are carried because "−4.2, discounted from −18 because it is 95 months
   * old" is a reviewer-checkable statement and "−4.2" alone is not.
   *
   * `supersededBy` marks an event that scored ZERO on purpose — the worst event
   * in its family already priced the same behaviour. These are shown, greyed, so
   * the reviewer can see what was considered rather than wondering what was
   * silently dropped.
   */
  historyFindings?: Array<{
    code: string;
    detail: string;
    impact?: string | null;
    deduction: number;
    baseDeduction?: number | null;
    recencyMultiplier?: number | null;
    ageMonths?: number | null;
    supersededBy?: string | null;
  }>;
  /** Agent 2: the banded counters behind the findings, for the hover detail. */
  historyContext?: Array<{ label: string; value: string }>;
  /** Agent 2: 100 − total deductions, so the arithmetic can be checked. */
  historyScoreWorking?: { subscore: number; totalDeduction: number } | null;
  /**
   * Agent 2: what that 0-100 repayment score is worth on the FINAL score.
   *
   * Without this the two cards read as a contradiction — Agent 2 says 91/100 and
   * Agent 4 says −0.7, with nothing on screen explaining that repayment history
   * is only worth `weight` of the final 100. Carrying the row here lets Agent 2's
   * own panel close that gap where the question is actually asked.
   */
  historyEffect?: LoanReviewAdjustment | null;
  /** Agent 1: the five gate domains it actually scored. */
  domainScores?: Array<{ domain: string; label: string; score: number; cardScore?: number | null; cappedByStatus?: boolean }>;
  /** Agent 1: the composite of those five (outside signals excluded). */
  score?: number | null;
  /** Agent 4: how the final score was reached — one row per outside signal. */
  adjustments?: LoanReviewAdjustment[];
  /** Agent 4: the number the lane came from. */
  finalScore?: number | null;
  /** Agent 4: bullets from the narration. */
  keyPoints?: string[];
  /** Agent 4: whether the summary was written by the model or is the fallback. */
  narrativeSource?: string | null;
}

/** One outside signal applied to the Agent-1 score. */
export interface LoanReviewAdjustment {
  adjuster: string;
  label: string;
  /** The signal's own 0-100 score. Null when it did not count. */
  subscore: number | null;
  weight: number | null;
  /** Points added or removed. Negative is a deduction. */
  adjustment: number;
  counted: boolean;
  /** Why it did not count: weight_zero | gate_absent | gate_unavailable. */
  reason: string | null;
}

export type LoanReviewServiceKey = 'identity' | 'income' | 'employer' | 'bureau' | 'cross_check';

/** One row of a per-service table: what we received, what Loan Review did with it, and the result. */
export interface LoanReviewServiceRow {
  field: string;
  received: string;
  action: string;
  status: string;
}

export interface LoanReviewServiceSection {
  key: LoanReviewServiceKey;
  title: string;
  overallStatus: string;
  rows: LoanReviewServiceRow[];
  agentLine: { label: string; value: string } | null;
  /** Hover text for hero pills (reasons + compared values). */
  tooltipReasons: string[];
}

/** Where a hero reason/soft note should navigate on click. */
export type ReviewNoteAppTab = 'employer' | 'identity' | 'income';

/**
 * A more specific place inside the Income tab than "the tab" - drives which
 * Bank activity sub-view opens, so "gambling transactions were found" lands the
 * reviewer directly on the flagged rows instead of a blank Income tab they then
 * have to search themselves.
 */
export type IncomeSubTarget = 'gambling-debits';

/** Hero reason / soft-difference bullet with optional evidence + jump target. */
export interface ReviewReasonNote {
  text: string;
  hint?: string | null;
  /** Cross-source matrix field to expand/highlight on click. */
  targetField?: string | null;
  /** Admin application tab (e.g. Employer) — preferred over matrix when set. */
  appTab?: ReviewNoteAppTab | null;
  /** Sub-view within appTab to open directly, when one applies. */
  incomeSubTarget?: IncomeSubTarget | null;
}

/** One "What's happening" header chip. Mirrors a source card so the two cannot disagree. */
export interface LoanReviewStatusChip {
  key: string;
  label: string;
  /** Empty when the source produced no data. */
  status: string;
}

/** One check inside a source card, in business language. */
export interface SourceCardSubCheck {
  name: string;
  label: string;
  status: string;
  reasonCodes: string[];
  /** Plain-language "what we did → result". */
  plain: string;
  /** Comparison context (cross-checks): the field and the two values that differ. */
  field?: string | null;
  idValue?: string | null;
  otherValue?: string | null;
  otherSource?: string | null;
  /** Exact underlying value(s) revealed on hover (e.g. "Confidence: 0.91"). */
  hint?: string | null;
}

/** One check's contribution to a source score (for "how the score was calculated"). */
export interface ScoreContribution {
  label: string;
  status: string;
  /** Relative importance of this check. */
  weight: number;
  /** weight × status score (0 = fail … weight = pass). */
  contribution: number;
}

/** One source's contribution to the combined score. */
export interface CombinedDomainRow {
  /** Raw domain key (`identity`, `external`, …). The label is for reading; this is
   *  for deciding how to render — `external` is shown as points deducted. */
  domain: string;
  label: string;
  status: string;
  score: number;
  weight: number;
  contribution: number;
}

/** Per-source dashboard card: what we received → what we did → status. */
export interface SourceCardViewModel {
  key: string;
  source: string;
  signal: string;
  gateId: string;
  status: string;
  blocksStp: boolean;
  /** What this area contributed to the combined score, after any status cap. */
  weightedScore: number | null;
  /**
   * The area's own internal score (its sub-checks alone, before a status like
   * REVIEW caps its contribution). Equal to weightedScore except when a
   * zero-weight flag (e.g. gambling) capped the gate without touching any
   * scored sub-check - scoreBreakdown always sums to THIS, not to weightedScore.
   */
  internalScore: number | null;
  /** Labels of the checks whose status capped this card (backend analysis.cap_checks). */
  capChecks: string[];
  received: Array<{ label: string; value: string }>;
  subChecks: SourceCardSubCheck[];
  reasonCodes: string[];
  /** One-line plain-English outcome for the card. */
  summary: string;
  /** Per-check contributions to this source's score. */
  scoreBreakdown: ScoreContribution[];
}

/** Combined Agent-1 score across all sources. */
export interface CombinedScoreViewModel {
  score: number | null;
  band: string | null;
  /** Final routing lane: APPROVE | REVIEW | REJECT | FOLLOW_CREDIT_DECISION (legacy FOLLOW_PROVENIR). */
  lane: string | null;
  status: string | null;
  /** Plain-English "why this lane" from the backend. */
  decisionBasis: string | null;
  domainScores: Array<{
    domain: string;
    label: string;
    /** What the combined score counted — capped at the status score when capped. */
    score: number;
    /** What the area scored on its own card, before any cap. */
    cardScore: number | null;
    cappedByStatus: boolean;
    /** The gate status behind the cap, so its reason can be worded (review vs fail). */
    status: string | null;
    /** Checks that capped this area (backend breakdown cap_checks). */
    capChecks: string[];
  }>;
  /** Per-source contributions to the AGENT 1 score (five domains). */
  breakdown: CombinedDomainRow[];
  /** Areas that cost the most points, worst first. */
  topDrags: Array<{ label: string; pointsLost: number }>;
  /**
   * The five domains' own composite. `score` is the FINAL number, after
   * adjustments — so the breakdown rows sum to this, not to `score`. Showing the
   * rows against `score` would leave a gap a reviewer cannot account for.
   */
  agent1Score: number | null;
  /** Outside signals applied after Agent 1, each with the points it moved. */
  adjustments: LoanReviewAdjustment[];
}

export interface LoanReviewViewModel {
  outcomeLabel: LoanReviewOutcomeLabel;
  requestStatus: string | null;
  decision: string | null;
  primaryReasonCode: string | null;
  primaryReasonPlain: string;
  /** Plain-English why review / why approved for the hero */
  businessSummary: string | null;
  /** Blocking reason bullets for REVIEW */
  reviewReasonNotes: ReviewReasonNote[];
  /** Soft-flag bullets (approve + review) */
  softFlagNotes: ReviewReasonNote[];
  /**
   * Other REVIEW signals that did not alone block auto-approval
   * (e.g. employer gates on Approve, or when a name hard-stop capped the lane).
   */
  alsoNotedNotes: ReviewReasonNote[];
  /** Display label for the review queue (plain English). */
  reviewQueue: string | null;
  wouldBeStp: boolean | null;
  stpKillSwitch: string | null;
  processingTimeMs: number | null;
  attention: string[];
  gates: LoanReviewGateRow[];
  crossChecks: LoanReviewCrossCheckRow[];
  /** Single-source MiTek dedup — shown under Gates, not in pairwise Cross-checks */
  identityDedup: LoanReviewIdentityDedup | null;
  variations: string[];
  pipelineStages: LoanReviewPipelineStage[];
  agent1: LoanReviewAgentSlice;
  agent2: LoanReviewAgentSlice;
  agent3: LoanReviewAgentSlice;
  agent4: LoanReviewAgentSlice;
  technical: Array<{ label: string; value: string }>;
  errors: string[];
  hasGatesSummary: boolean;
  hasPipelineSummary: boolean;
  /** Per-source (Identity/Income/Employer/Bureau) tables of received signal → what Loan Review did. */
  services: LoanReviewServiceSection[];
  /** Header chips for the hero, derived from the same statuses the source cards show. */
  serviceChips: LoanReviewStatusChip[];
  /** Plain-language pipeline/STP context — no raw correlation/trace IDs. */
  orchestratorSummary: string[];
  /** Per-source cards (Identity / Income / Employer / Decision engine / Cross-check). */
  sourceCards: SourceCardViewModel[];
  /** Combined Agent-1 score across all sources (null on older runs). */
  combined: CombinedScoreViewModel | null;
}

const REASON_PLAIN: Record<string, string> = {
  // External employer context (Agent 3). Wording matches the backend's
  // REVIEW_REASON_TEMPLATES headlines so a reviewer sees the same sentence whether
  // the note came from the service or was rebuilt here for an older run.
  EMPLOYER_MASS_LAYOFF: 'The employer publicly announced significant layoffs',
  EMPLOYER_INSOLVENCY: 'The employer shows signs of insolvency or creditor protection',
  EMPLOYER_CLOSURE: 'The employer announced a closure or site shutdown',
  EMPLOYER_WAGE_PAYMENT_ISSUE: 'The employer was reported to have missed or delayed paying staff',
  EMPLOYER_FRAUD_OR_REGULATORY: 'The employer faces a fraud allegation or regulatory action',
  EMPLOYER_RESTRUCTURING: 'The employer is restructuring',
  EMPLOYER_LABOUR_ACTION: 'The employer is involved in a strike or labour dispute',
  EMPLOYER_HIRING_FREEZE: 'The employer announced a hiring freeze',
  BANK_HOLDER_IDENTITY_MISMATCH: 'Bank account holder name does not match ID',
  INCOME_VERDICT_FAIL: 'Income verification failed',
  INCOME_VERDICT_REVIEW: 'Income verification requires human review',
  // Same wording as the backend's _CODE_PLAIN / REVIEW_REASON_TEMPLATES.
  INCOME_GAMBLING_DETECTED: 'Gambling transactions were found in the bank statement',
  INCOME_PAYDAY_STACKING_HIGH: 'Several payday loans are being serviced at the same time',
  MANDATORY_EVIDENCE_MISSING: 'Required evidence is missing',
  BUREAU_DOB_MISSING: 'Bureau date of birth is missing',
  BUREAU_NOT_APPLICABLE_PRODUCT: 'Not applicable — Payday Loans have no credit-bureau check',
  ID_DOB_MISSING: 'ID date of birth is missing',
  BUREAU_NAME_MISSING: 'Bureau name is missing',
  ID_NAME_MISSING: 'ID name is missing',
  ID_ADDRESS_MISSING: 'ID address is missing',
  BANK_HOLDER_NAME_MISSING: 'Bank account holder name is missing',
  IDENTITY_EVIDENCE_MISSING: 'Identity verification evidence is missing',
  INCOME_EVIDENCE_MISSING: 'Income verification evidence is missing',
  EMPLOYER_EVIDENCE_MISSING: 'Employer verification evidence is missing',
  PROVENIR_NOT_APPROVED: 'Credit decision is not approved',
  PROVENIR_UNAVAILABLE: 'Credit / bureau evidence unavailable',
  CREDIT_DECISION_NOT_APPROVED: 'Credit decision is not approved',
  CREDIT_DECISION_UNAVAILABLE: 'Credit / bureau evidence unavailable',
  MITEK_VERIFICATION_FAILED: 'Identity verification failed',
  IDENTITY_VERIFICATION_FAILED: 'Identity verification failed',
  BUREAU_FRAUD_ALERT: 'Bureau fraud alert',
  BUREAU_NAME_MISMATCH: 'Bureau name does not match ID',
  BUREAU_DOB_MISMATCH: 'Bureau date of birth does not match ID',
  IDENTITY_EXCESSIVE_ATTEMPTS: 'Too many identity verification attempts',
  FLINKS_ADDRESS_VARIATION: 'Bank address differs from ID (soft difference)',
  FLINKS_EMAIL_VARIATION: 'Bank email differs from the registered email (soft difference)',
  FLINKS_PHONE_VARIATION: 'Bank phone differs from the registered phone number (soft difference)',
  BANK_ADDRESS_VARIATION: 'Bank address differs from ID (soft difference)',
  BANK_EMAIL_VARIATION: 'Bank email differs from the registered email (soft difference)',
  BANK_PHONE_VARIATION: 'Bank phone differs from the registered phone number (soft difference)',
  EMPLOYER_LEGITIMACY_FAIL:
    'Could not confirm the employer looks like a real, trustworthy workplace (see Employer tab)',
  EMPLOYER_STRUCTURAL_FAIL:
    'Job title, industry, or work setup does not look consistent with the employer (see Employer tab)',
  EMPLOYER_EXISTENCE_FAIL: 'Could not confirm the employer exists',
  EMPLOYER_LINKAGE_FAIL: 'Could not clearly link applicant to employer',
  EMPLOYER_LINKAGE_AI_CONFIRMED:
    "Employer link accepted — the AI payroll check confirmed the deposit is this employer's payroll (soft difference)",
  EMPLOYER_PAYOR_MISMATCH:
    'Salary deposits do not come from the employer the applicant named (see Employer tab)',
  EMPLOYER_FRAUD_SIGNAL: 'Employer fraud or risk signals were raised',
  EMPLOYER_TEMPORAL_FAIL: 'Employer timing / tenure checks did not clear',
  EMPLOYER_DOCTRINE_FAIL: 'Employer verification did not clear',
  STP_KILL_SWITCH_OFF: 'Auto-approve kill switch is off',
  GATE_SKIPPED: 'Gate was skipped in this environment',
  AGENT1_SCORE_REVIEW: 'Agent-1 score is in the review band',
  AGENT1_SCORE_BELOW_REJECT_FLOOR: 'Agent-1 score is below the reject floor',
  // Device / visitor (G08) — plain language for fraud reviewers
  VISITOR_MULTI_APP:
    'This same browser/device was already used on earlier loan applications',
  VISITOR_MULTI_USER:
    'This same browser/device was used by more than one customer account',
  VISITOR_PRIOR_DECLINED:
    'This same browser/device was used on a previously declined application',
  VISITOR_PRIOR_DEVICE_REVIEW:
    'This same browser/device was flagged for device risk on earlier applications',
  VISITOR_MULTI_IP:
    'This same browser/device connected from several different IP addresses',
  VISITOR_MULTI_COUNTRY:
    'This same browser/device connected from more than one country',
  DEVICE_RISK_REVIEW:
    'Device risk checks flagged this session for review (for example VPN or unusual activity)',
  DEVICE_RISK_BLOCK:
    'Device risk checks blocked this session',
};

/** Optional combined Agent-1 score for richer reason copy. */
export type PlainReasonScoreContext = {
  score?: number | null;
  band?: string | null;
};

const FALLBACK_APPROVE_SUMMARY =
  'This application cleared automated checks and was approved for the auto-funding path.';

/** Soft-flag bullets when agent did not publish softFlagNotes (older runs). */
const SOFT_FLAG_NOTE: Record<string, string> = {
  FLINKS_PHONE_VARIATION: 'bank phone differs from the registered phone number',
  FLINKS_EMAIL_VARIATION: 'bank email differs from the registered email',
  FLINKS_ADDRESS_VARIATION: 'bank address differs from ID address',
  BANK_PHONE_VARIATION: 'bank phone differs from the registered phone number',
  BANK_EMAIL_VARIATION: 'bank email differs from the registered email',
  BANK_ADDRESS_VARIATION: 'bank address differs from ID address',
  BUREAU_NAME_VARIATION: 'bureau name has a soft difference from the ID',
  BUREAU_ADDRESS_VARIATION: 'bureau address has a soft difference from the ID',
  BUREAU_ADDRESS_PREVIOUS: 'bureau shows a previous address difference',
  EMPLOYER_ALIAS_MAPPED: 'payor matched employer via an alias mapping',
  PAYROLL_PROCESSOR_MAPPED: 'payor is a payroll processor mapped to the employer',
  EMPLOYER_LINKAGE_AI_CONFIRMED:
    "the AI linkage check confirmed the bank deposit is this employer's payroll, although the names did not match exactly",
  NAME_MIDDLE_INITIAL_VARIATION: 'bank holder name has a soft difference from the ID',
  NAME_TOKEN_ORDER_VARIATION: 'bank holder name has a soft difference from the ID',
  IDENTITY_NAME_VARIATION: 'the legal name is spelled slightly differently across sources',
  IDENTITY_NAME_SURNAME_CHANGE:
    'a source shows a different surname, confirmed by a matching date of birth',
  INCOME_CLASS_WEAK: 'income classification is weaker than ideal',
  EMPLOYER_NOT_RUN: 'employer verification was not fully run',
};

/** Strip legacy paragraphs that inlined lists into businessSummary. */
export function stripLegacySoftFlagParagraph(summary: string | null): string | null {
  if (!summary) return null;
  const stripped = summary
    .replace(
      /\s*(The following soft differences|These soft differences)[\s\S]*$/i,
      '',
    )
    .replace(/\s*Also noted:[^.]*(?:;[^.]*)*\./gi, '')
    .trim();
  return stripped || summary;
}

function isSoftFlagCode(code: string | null | undefined): boolean {
  return Boolean(code && SOFT_FLAG_NOTE[code]);
}

function looksLikeSoftDifferenceNote(note: string): boolean {
  return /\(soft difference\)|soft difference/i.test(note);
}

/**
 * Gates whose status answers "did the check run", not "did it find a problem".
 *
 * Mirror of the backend's `_ADVISORY_SCORED_GATES`. G12 reports PASS even when it
 * found verified layoffs — severity lives in its score — so filtering on status
 * alone drops its codes, and a file sent to review *because of* employer news
 * renders an empty reason list. Kept in sync with business_summary.py.
 */
const ADVISORY_SCORED_GATE_IDS = new Set(['G12_EXTERNAL_CONTEXT']);

/** Client-side fallback when agent did not publish reviewReasonNotes. Soft flags excluded. */
export function buildReviewReasonNotes(opts: {
  gateRows: LoanReviewGateRow[];
  primaryReasonCode: string | null;
  scoreCtx?: PlainReasonScoreContext | null;
}): string[] {
  const notes: string[] = [];
  const seenCodes = new Set<string>();
  const seenNotes = new Set<string>();

  const add = (code: string | null, gateTitle?: string) => {
    if (!code || seenCodes.has(code) || isSoftFlagCode(code)) return;
    const plain = plainReason(code, opts.scoreCtx);
    if (looksLikeSoftDifferenceNote(plain)) return;
    const note = gateTitle ? `${gateTitle}: ${plain}` : plain;
    if (seenNotes.has(note) || looksLikeSoftDifferenceNote(note)) return;
    seenCodes.add(code);
    seenNotes.add(note);
    notes.push(note);
  };

  if (opts.primaryReasonCode && !isSoftFlagCode(opts.primaryReasonCode)) {
    add(opts.primaryReasonCode);
  }

  for (const g of opts.gateRows) {
    const advisory = ADVISORY_SCORED_GATE_IDS.has(g.gateId);
    if (!advisory && !['FAIL', 'REVIEW', 'UNAVAILABLE', 'ERROR'].includes(g.status)) continue;
    for (const code of g.reasonCodes) {
      // No gate title prefix for advisory gates: "External context: The employer
      // announced layoffs" reads as a system label attached to a plain sentence.
      add(code, advisory ? undefined : g.title);
      if (notes.length >= 8) return notes;
    }
  }

  return notes;
}

/**
 * Approve-path also-noted bullets: gate REVIEW/FAIL codes that did not block STP.
 * Prefer plain reason phrases (no gate-title prefix) to match agent publish shape.
 */
export function buildAlsoNotedNotesForApprove(opts: {
  gateRows: LoanReviewGateRow[];
  scoreCtx?: PlainReasonScoreContext | null;
}): string[] {
  const notes: string[] = [];
  const seenCodes = new Set<string>();
  const seenNotes = new Set<string>();

  for (const g of opts.gateRows) {
    if (!['FAIL', 'REVIEW', 'UNAVAILABLE', 'ERROR'].includes(g.status)) continue;
    for (const code of g.reasonCodes) {
      if (!code || seenCodes.has(code) || isSoftFlagCode(code)) continue;
      const note = plainReason(code, opts.scoreCtx);
      if (!note || looksLikeSoftDifferenceNote(note) || seenNotes.has(note)) continue;
      seenCodes.add(code);
      seenNotes.add(note);
      notes.push(note);
      if (notes.length >= 6) return notes;
    }
  }
  return notes;
}

/** Replace misleading "cleared employer checks" copy when also-noted REVIEW signals remain. */
export function softenApproveSummaryForAlsoNoted(
  summary: string | null,
  alsoNotedCount: number,
): string | null {
  if (!summary || alsoNotedCount <= 0) return summary;
  if (!/(cleared|passed all).*(employer|automated checks)/i.test(summary)) return summary;
  return (
    'This application was approved for the auto-funding path. ' +
    'Some verification signals needed a closer look but did not block approval.'
  );
}

function gateStatusDisplay(status: string | null | undefined): string {
  if (!status) return '—';
  const s = status.toUpperCase();
  if (s === 'PASS') return 'Pass';
  if (s === 'PASS_WITH_VARIATION') return 'Soft difference';
  if (s === 'REVIEW') return 'Needs review';
  if (s === 'FAIL') return 'Fail';
  if (s === 'UNAVAILABLE') return 'Unavailable';
  if (s === 'ERROR') return 'Error';
  if (s === 'SKIPPED') return 'Skipped';
  return status;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

const IMPACT_RANK: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

function mapExternalFindings(value: unknown): LoanReviewExternalFinding[] {
  return asArray(value)
    .map((raw) => asRecord(raw))
    .filter((r): r is Record<string, unknown> => r != null)
    .map((r) => ({
      code: str(r.code) ?? '—',
      impact: str(r.impact) ?? str(r.severity) ?? '—',
      deduction: num(r.deduction),
      eventDate: str(r.eventDate),
      publisher: str(r.publisher),
      sourceDomain: str(r.sourceDomain),
      sourceUrl: str(r.sourceUrl),
      sourceTier: str(r.sourceTier),
      employerMatchConfidence: num(r.employerMatchConfidence),
      evidenceQuote: str(r.evidenceQuote),
    }))
    // Worst first, then most recent — a reviewer reads top-down and should hit the
    // finding that cost the most score first.
    .sort((a, b) => {
      const byImpact = (IMPACT_RANK[a.impact] ?? 9) - (IMPACT_RANK[b.impact] ?? 9);
      if (byImpact !== 0) return byImpact;
      return (b.eventDate ?? '').localeCompare(a.eventDate ?? '');
    });
}

/**
 * The five domains Agent 1 scored, labelled and in a fixed reading order.
 *
 * Order is fixed rather than taken from the payload so the grid reads identically
 * on every application — rows that move between files are hard to scan.
 */
/** Published status label, else the matching gate row's status. */
function statusOrGate(
  published: unknown,
  gateId: string,
  gateRows: LoanReviewGateRow[],
): string {
  const fromSlice = gateStatusDisplay(str(published));
  if (fromSlice !== '—') return fromSlice;
  return gateStatusDisplay(gateRows.find((g) => g.gateId === gateId)?.status);
}


/**
 * Fill in the score decomposition for payloads that do not carry it.
 *
 * Evaluations persisted before the backend split the score into
 * "Agent 1 + adjustments" have a final score and five breakdown rows, and nothing
 * linking them. Rendered as-is that table shows rows summing to ~99 under a total
 * of ~93, with the missing points unaccounted for — exactly the arithmetic the
 * panel exists to make checkable.
 *
 * Both missing pieces are recoverable from the payload itself:
 *   agent1Score = the breakdown rows' own sum (that is what they sum to)
 *   adjustment  = final score − agent1Score
 *
 * External context is the only adjuster with a non-zero weight, so a gap can only
 * have come from it — its subscore is read from Agent 3's own block when present.
 * Nothing is invented: if there is no gap, no row is added.
 */
function withDerivedAdjustments(
  combined: CombinedScoreViewModel | null,
  agent3Raw: Record<string, unknown> | null,
): CombinedScoreViewModel | null {
  if (!combined) return null;

  const rowSum = combined.breakdown.length
    ? Math.round(combined.breakdown.reduce((t, b) => t + b.contribution, 0) * 100) / 100
    : null;
  const agent1Score = combined.agent1Score ?? rowSum;
  if (combined.adjustments.length || agent1Score == null || combined.score == null) {
    return { ...combined, agent1Score };
  }

  const gap = Math.round((combined.score - agent1Score) * 100) / 100;
  if (Math.abs(gap) < 0.05) return { ...combined, agent1Score };

  const external = asRecord(agent3Raw?.externalSubscore);
  return {
    ...combined,
    agent1Score,
    adjustments: [
      {
        adjuster: 'external',
        label: ADJUSTER_LABELS.external,
        subscore: num(external?.score),
        weight: num(external?.weight),
        adjustment: gap,
        counted: true,
        reason: null,
      },
    ],
  };
}


/** `superseded_by:INTERNAL_DELINQUENCY_ETD90` -> `INTERNAL_DELINQUENCY_ETD90`. */
function supersededWinner(droppedReason: string | null | undefined): string | null {
  if (!droppedReason) return null;
  const m = /^superseded_by:(.+)$/.exec(droppedReason);
  return m ? m[1] : droppedReason;
}

const AGE_BAND_PLAIN: Record<string, string> = {
  NEW: 'within the last month',
  UNDER_1Y: 'less than a year ago',
  ONE_TO_3Y: '1–3 years ago',
  THREE_TO_7Y: '3–7 years ago',
  OVER_7Y: 'more than 7 years ago',
};

const COUNT_BAND_PLAIN: Record<string, string> = {
  NONE: 'none',
  ONE: '1',
  TWO_THREE: '2–3',
  FOUR_SIX: '4–6',
  SEVEN_PLUS: '7 or more',
};

const DPD_BAND_PLAIN: Record<string, string> = {
  NONE: 'not past due',
  DPD_1_29: '1–29 days past due',
  DPD_30_59: '30–59 days past due',
  DPD_60_89: '60–89 days past due',
  DPD_90_PLUS: '90+ days past due',
};

/**
 * The banded counters behind the subscore, for the expandable detail.
 *
 * This is the closest thing to "which loans and when" that this payload can
 * honestly offer. Loan ids, amounts, funding dates and exact delinquency months
 * are dropped before the record ever reaches loan review — deliberately, so a
 * reviewer's screen is not a second copy of the servicing system. What survives
 * is the shape of the book and the age of the behaviour, which is what the
 * subscore was actually computed from. A reviewer who needs loan-level detail
 * goes to the servicing screen; this panel explains the score.
 *
 * Rows are omitted when they are zero/absent rather than rendered as "none":
 * a wall of zeroes buries the two or three counters that matter.
 */
function historyContextRows(own: Record<string, unknown> | null | undefined): Array<{
  label: string;
  value: string;
}> {
  const r = asRecord(own?.rollup);
  if (!r) return [];

  const rows: Array<{ label: string; value: string }> = [];
  const push = (label: string, value: string | null | undefined) => {
    if (value) rows.push({ label, value });
  };
  const band = (key: string, map: Record<string, string>): string | null => {
    const raw = str(r[key]);
    if (!raw || raw === 'NONE') return null;
    return map[raw] ?? raw;
  };
  const count = (key: string): string | null => {
    const n = num(r[key]);
    return n && n > 0 ? String(n) : null;
  };

  push('Installment loans', band('installmentLoansBand', COUNT_BAND_PLAIN));
  push('Payday advances', band('paydayLoanCountBand', COUNT_BAND_PLAIN));
  push('Open loans', band('openLoansBand', COUNT_BAND_PLAIN));
  push('Paid off', band('paidOffBand', COUNT_BAND_PLAIN));
  push('Written off', count('writtenOffCount'));
  push('Worst delinquency ever', str(r.worstEverEtd));
  push('Worst delinquency was', band('monthsSinceWorstDelinquencyBand', AGE_BAND_PLAIN));
  push('Currently', band('currentDpdBand', DPD_BAND_PLAIN));
  push('First-payment defaults', count('firstPaymentDefaultCount'));
  push('Payday payments bounced', count('paydayBounceCount'));
  push('Payday advances open past due', count('paydayActivePastDueCount'));
  push('Payday advances closed late', count('paydayClosedLateCount'));
  push('Bounced payment-plan instalments', count('paydayPaymentPlanBouncedRows'));
  push('Collections / bankruptcy / legal records', count('paydayRisAdverseCount'));
  push('Rollover use', band('paydayRolloverBand', COUNT_BAND_PLAIN));
  push('Last borrowed', band('monthsSinceLastLoanBand', AGE_BAND_PLAIN));
  push('First borrowed', band('monthsSinceFirstLoanBand', AGE_BAND_PLAIN));

  // Provenance last, and only when it is bad news. A stale snapshot must never
  // be read as a clean record.
  if (r.snapshotStale === true) {
    const days = num(r.snapshotAgeDays);
    rows.push({
      label: 'Data freshness',
      value: days
        ? `Stale — legacy snapshot is ${days} days old. Treat as incomplete, not clean.`
        : 'Stale — legacy snapshot is out of date. Treat as incomplete, not clean.',
    });
  }
  if (r.sourcesOk === false) {
    rows.push({
      label: 'Data completeness',
      value: 'Some legacy sources did not answer — this record may be incomplete.',
    });
  }

  return rows;
}

/**
 * Agent 2's `availability`, in words an operator can act on.
 *
 * The three "we could not look" states are deliberately NOT collapsed into one
 * label: a pending linkage is someone's job to resolve, an outage is someone
 * else's, and neither means the customer is clean.
 */
function describeHistoryAvailability(availability: string | null): string {
  switch (availability) {
    case 'RESOLVED':
      return 'Found';
    case 'NO_LEGACY_MATCH':
      return 'New customer — no prior record';
    case 'PENDING':
      return 'Unavailable — identity linkage under review';
    case 'UNAVAILABLE':
      return 'Unavailable — legacy system did not answer';
    case 'NOT_REQUESTED':
      return 'Not checked';
    case 'DISABLED':
      return 'Not checked — lookup disabled';
    case 'ERROR':
      return 'Unavailable — lookup failed';
    default:
      return '—';
  }
}

/**
 * `cardScore` is what the area scored on its own card; `score` is what the combined
 * score counted. They differ when a check needs review, because such a check is capped
 * at its status score and cannot earn full marks. Showing only the counted number put
 * 82 on the Employer card and 50 in this chip with nothing connecting them, which reads
 * as an arithmetic bug rather than a rule.
 */
function mapAgent1DomainScores(
  published: unknown,
  combinedRaw: Record<string, unknown> | null,
): Array<{
  domain: string;
  label: string;
  score: number;
  cardScore: number | null;
  cappedByStatus: boolean;
}> {
  const ORDER = ['identity', 'income', 'employment', 'bureau_credit', 'cross_check'];
  const byDomain = new Map<string, number>();
  const cardByDomain = new Map<string, number>();
  const cappedDomains = new Set<string>();

  for (const row of asArray(published)) {
    const r = asRecord(row);
    const domain = str(r?.domain);
    const score = num(r?.score);
    if (domain && score != null) byDomain.set(domain, score);
  }
  // Fall back to the combined block, which carries the same numbers — the DB path
  // does not always populate the agent1 slice.
  if (byDomain.size === 0) {
    const scores = asRecord(combinedRaw?.domain_scores) ?? {};
    for (const [k, v] of Object.entries(scores)) {
      const score = num(v);
      if (score != null) byDomain.set(k, score);
    }
  }

  // The uncapped number lives only on the breakdown rows.
  for (const row of asArray(combinedRaw?.breakdown)) {
    const r = asRecord(row);
    const domain = str(r?.domain);
    if (!domain) continue;
    const card = num(r?.card_score);
    if (card != null) cardByDomain.set(domain, card);
    if (r?.capped_by_status === true) cappedDomains.add(domain);
  }

  return ORDER.filter((d) => byDomain.has(d)).map((d) => ({
    domain: d,
    label: DOMAIN_LABELS[d] ?? d.replace(/_/g, ' '),
    score: byDomain.get(d)!,
    cardScore: cardByDomain.get(d) ?? null,
    cappedByStatus: cappedDomains.has(d),
  }));
}

/** Matches AGENT1_COMBINED.weights in svc-loan-review-agent signal_weights.v1.yaml. */
const AGENT1_DOMAIN_WEIGHTS: Record<string, number> = {
  identity: 22,
  income: 22,
  employment: 16,
  bureau_credit: 22,
  cross_check: 18,
};

function formatAgent1Points(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Weighted points toward the Agent 1 total (e.g. Identity 22 of 22), not the
 * raw 0–100 domain scores already shown on the scoring chips above this card.
 */
function mapAgent1ContributionLines(
  domainScores: Array<{ domain: string; label: string; score: number }>,
  combinedRaw: Record<string, unknown> | null,
): Array<{ label: string; value: string }> {
  const byDomain = new Map<string, { contribution: number | null; weight: number | null }>();
  for (const row of asArray(combinedRaw?.breakdown)) {
    const r = asRecord(row);
    const domain = str(r?.domain);
    if (!domain) continue;
    byDomain.set(domain, {
      contribution: num(r?.contribution),
      weight: num(r?.weight),
    });
  }
  return domainScores.map((d) => {
    const row = byDomain.get(d.domain);
    const weight = row?.weight ?? AGENT1_DOMAIN_WEIGHTS[d.domain] ?? 0;
    const contribution =
      row?.contribution != null ? row.contribution : (d.score / 100) * weight;
    return { label: d.label, value: formatAgent1Points(contribution) };
  });
}

/**
 * One row per outside signal applied to the Agent-1 score.
 *
 * Uncounted rows are kept: "found nothing", "weight 0" and "never ran" otherwise
 * render as an identical blank, which is the ambiguity that caused the most
 * confusion in the old panel.
 */
function mapAdjustments(published: unknown, combinedRaw: Record<string, unknown> | null): LoanReviewAdjustment[] {
  const rows = asArray(published).length ? asArray(published) : asArray(combinedRaw?.adjustments);
  const out: LoanReviewAdjustment[] = [];
  for (const row of rows) {
    const r = asRecord(row);
    const adjuster = str(r?.adjuster);
    if (!adjuster) continue;
    out.push({
      adjuster,
      label: ADJUSTER_LABELS[adjuster] ?? adjuster.replace(/_/g, ' '),
      subscore: num(r?.subscore),
      weight: num(r?.weight),
      adjustment: num(r?.adjustment) ?? 0,
      counted: r?.counted === true,
      reason: str(r?.reason),
    });
  }
  // Counted first, then biggest effect — an uncounted row is context, not a cause.
  return out.sort((a, b) => {
    if (a.counted !== b.counted) return a.counted ? -1 : 1;
    return Math.abs(b.adjustment) - Math.abs(a.adjustment);
  });
}

const ADJUSTER_LABELS: Record<string, string> = {
  external: 'External employer context',
  history: 'Repayment history',
};

/** Why an adjuster contributed nothing — three states that otherwise look alike. */
export const ADJUSTER_SKIP_REASON: Record<string, string> = {
  weight_zero: 'recorded, not counted (weight 0)',
  gate_absent: 'not available for this application',
  gate_unavailable: 'research did not run',
};


function mapExternalSubscore(value: unknown): LoanReviewExternalSubscore | null {
  const r = asRecord(value);
  if (!r) return null;
  return {
    score: num(r.score),
    weight: num(r.weight),
    adjustment: num(r.adjustment) ?? num(r.contribution),
    contribution: num(r.contribution) ?? num(r.adjustment),
    agent1Score: num(r.agent1Score),
    notCountedReason: str(r.notCountedReason),
    counted: r.counted === true,
    compositeScore: num(r.compositeScore),
    gateStatus: str(r.gateStatus),
    discardedCount: num(r.discardedCount) ?? 0,
    researchedAt: str(r.researchedAt),
    rawScore: num(r.rawScore),
    floor: num(r.floor),
    totalDeduction: num(r.totalDeduction),
  };
}

/**
 * One line summarising what Agent 3 did, replacing the old `Confidence` row.
 *
 * That row showed `ExternalContext.confidence` — a number the model produces during
 * synthesis, before any finding has been qualified, so it cannot describe the
 * findings it sat beside. These are facts instead.
 */
function describeExternalCoverage(
  sub: LoanReviewExternalSubscore | null,
  findingCount: number,
): string {
  if (!sub) return '—';
  if (sub.gateStatus === 'UNAVAILABLE') return 'Not researched';

  // Deliberately no research date. Every finding already carries the date of the
  // event itself, which is the date that decides anything; a second date beside it
  // only invites "which one am I reading?".
  const parts: string[] = [];
  parts.push(findingCount === 0 ? 'No findings' : `${findingCount} finding${findingCount === 1 ? '' : 's'}`);
  if (sub.discardedCount > 0) parts.push(`${sub.discardedCount} discarded`);
  return parts.join(' · ');
}

const NAME_HARD_STOP_CODES = new Set([
  'BANK_HOLDER_IDENTITY_MISMATCH',
  'BUREAU_NAME_MISMATCH',
  'BUREAU_DOB_MISMATCH',
]);

const QUEUE_DISPLAY: Record<string, string> = {
  CROSS_CHECK_REVIEW: 'Cross-check',
  BANK_OWNERSHIP_REVIEW: 'Bank ownership',
  EMPLOYMENT_REVIEW: 'Employment',
  EMPLOYER_REVIEW: 'Employment',
  INCOME_REVIEW: 'Income',
  IDENTITY_REVIEW: 'Identity',
  BUREAU_IDENTITY_REVIEW: 'Bureau identity',
  CREDIT_AFFORDABILITY_REVIEW: 'Credit',
  CREDIT_DECLINE_REVIEW: 'Credit decline',
  TECHNICAL_REMEDIATION: 'Technical',
};

/** Plain queue label for the hero chip. */
export function formatReviewQueue(queue: string | null | undefined): string | null {
  if (!queue) return null;
  const key = queue.trim().toUpperCase();
  return QUEUE_DISPLAY[key] ?? queue.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Align queue with primary name hard-stop (older runs used biggest score-drag domain). */
export function alignReviewQueue(
  queue: string | null,
  primaryReasonCode: string | null,
): string | null {
  const primary = (primaryReasonCode || '').toUpperCase();
  if (primary === 'BANK_HOLDER_IDENTITY_MISMATCH' || primary === 'BUREAU_NAME_MISMATCH') {
    return 'CROSS_CHECK_REVIEW';
  }
  if (primary === 'BUREAU_DOB_MISMATCH' || primary === 'BUREAU_DOB_MISSING') {
    return 'BUREAU_IDENTITY_REVIEW';
  }
  return queue;
}

function looksLikeEmployerReviewNote(note: string): boolean {
  return /see employer tab|employer exists|trustworthy workplace|work setup|consistent with the employer|employment/i.test(
    note,
  );
}

/** Display 0–1 rates as percent; values already >1 treated as percent points. */
export function formatRateAsPercent(value: unknown): string | null {
  const n = num(value);
  if (n == null) return null;
  const pct = n > 1 ? n : n * 100;
  const rounded = Math.round(pct * 10) / 10;
  const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${label}%`;
}

function bool(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  return null;
}

export function plainReason(
  code: string | null | undefined,
  scoreCtx?: PlainReasonScoreContext | null,
): string {
  if (!code) return 'No primary reason recorded';
  const upper = code.toUpperCase();
  const score =
    scoreCtx?.score != null && Number.isFinite(scoreCtx.score)
      ? Math.round(scoreCtx.score)
      : null;
  const band = typeof scoreCtx?.band === 'string' ? scoreCtx.band.trim() : '';
  const bandSuffix = band ? ` (${band} band)` : '';

  if (upper === 'AGENT1_SCORE_REVIEW') {
    if (score != null) {
      return `Agent-1 score ${score}${bandSuffix} — below auto-approve threshold`;
    }
    return REASON_PLAIN.AGENT1_SCORE_REVIEW;
  }
  if (upper === 'AGENT1_SCORE_BELOW_REJECT_FLOOR') {
    if (score != null) {
      return `Agent-1 score ${score}${bandSuffix} — below reject floor`;
    }
    return REASON_PLAIN.AGENT1_SCORE_BELOW_REJECT_FLOOR;
  }

  return REASON_PLAIN[upper] ?? REASON_PLAIN[code] ?? code.replace(/_/g, ' ').toLowerCase();
}

/** When the agent published a bare "agent1 score review" note, attach the numeric score. */
export function enrichAgent1ScoreNote(
  note: string,
  scoreCtx?: PlainReasonScoreContext | null,
): string {
  const score =
    scoreCtx?.score != null && Number.isFinite(scoreCtx.score)
      ? Math.round(scoreCtx.score)
      : null;
  if (score == null) return note;
  if (/\bAgent-1 score\s+\d+/i.test(note)) return note;
  if (/agent[- ]?1 score review/i.test(note) || /agent[- ]?1 score is in the review band/i.test(note)) {
    return plainReason('AGENT1_SCORE_REVIEW', scoreCtx);
  }
  if (/agent[- ]?1 score is below the reject floor/i.test(note)) {
    return plainReason('AGENT1_SCORE_BELOW_REJECT_FLOOR', scoreCtx);
  }
  return note;
}

/** Client-side fallback when agent did not publish businessSummary (older runs). */
export function fallbackBusinessSummary(opts: {
  decision: string | null;
  primaryReasonCode: string | null;
  scoreCtx?: PlainReasonScoreContext | null;
}): string | null {
  const d = (opts.decision || '').toUpperCase();
  if (d === 'APPROVE') {
    return FALLBACK_APPROVE_SUMMARY;
  }
  if (d === 'REVIEW' || d === 'DECLINE' || d === 'HUMAN_REVIEW') {
    const why = plainReason(opts.primaryReasonCode, opts.scoreCtx);
    if (!opts.primaryReasonCode) {
      return 'This application was sent for human review. Please review verification evidence before funding.';
    }
    const whyClause = why.charAt(0).toLowerCase() + why.slice(1);
    return (
      `A person needs to review this application before funding because ${whyClause}. ` +
      `Identity, income, and credit may still look fine — check Device intelligence and the loan-review reasons below.`
    );
  }
  return null;
}

/** True when published summary is too thin for the hero (bare code phrase or excerpt). */
export function isThinBusinessSummary(
  summary: string | null | undefined,
  primaryReasonCode: string | null,
  scoreCtx?: PlainReasonScoreContext | null,
): boolean {
  const s = summary?.trim();
  if (!s) return true;
  const plain = primaryReasonCode ? plainReason(primaryReasonCode, scoreCtx) : null;
  if (plain && s === plain) return true;
  // Bare code phrase without the numeric score is still thin when we can enrich it.
  if (
    primaryReasonCode &&
    scoreCtx?.score != null &&
    /agent[- ]?1 score review/i.test(s) &&
    !/\d/.test(s)
  ) {
    return true;
  }
  if (
    s.length < 90 &&
    !/sent for human review|cleared automated|Please review|Please confirm|Focus:/i.test(s)
  ) {
    return true;
  }
  // Agent/LLM sometimes echoes raw codes ("visitor multi app") — rewrite via fallback.
  if (
    primaryReasonCode &&
    /visitor multi(?:[- ]?app|[- ]?user|[- ]?ip|[- ]?country)|prior device review|device risk (?:review|block)|due to visitor/i.test(
      s,
    )
  ) {
    return true;
  }
  return false;
}

/** Pick hero paragraph: prefer agent-published summary unless fallback is richer. */
export function resolveBusinessSummary(opts: {
  decision: string | null;
  primaryReasonCode: string | null;
  published: Array<string | null | undefined>;
  scoreCtx?: PlainReasonScoreContext | null;
}): string | null {
  for (const candidate of opts.published) {
    const s = candidate?.trim();
    if (s && !isThinBusinessSummary(s, opts.primaryReasonCode, opts.scoreCtx)) {
      return s;
    }
  }
  return fallbackBusinessSummary({
    decision: opts.decision,
    primaryReasonCode: opts.primaryReasonCode,
    scoreCtx: opts.scoreCtx,
  });
}

/** Admin display only — API keeps machine status (e.g. PASS_WITH_VARIATION). */
export function displayStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  const s = status.toUpperCase();
  if (s === 'PASS_WITH_VARIATION') return 'Soft difference';
  if (s === 'PASS') return 'Pass';
  if (s === 'REVIEW') return 'Needs review';
  if (s === 'FAIL') return 'Fail';
  if (s === 'SKIPPED' || s === 'SKIP') return 'Skipped';
  return status;
}

/** Bank twin for bureau pairwise rows (same conceptual field). Legacy + capability names. */
const BUREAU_SIBLING_MATCH: Record<string, string> = {
  MITEK_VS_BUREAU_NAME: 'MITEK_VS_FLINKS_HOLDER',
  MITEK_VS_BUREAU_ADDRESS: 'MITEK_VS_FLINKS_ADDRESS',
  ID_VS_BUREAU_NAME: 'ID_VS_BANK_HOLDER',
  ID_VS_BUREAU_ADDRESS: 'ID_VS_BANK_ADDRESS',
};

/** Normalize capability match types; keep legacy vendor-prefixed names readable. */
export function normalizeMatchType(matchType: string): string {
  const mt = (matchType || '').toUpperCase();
  const aliases: Record<string, string> = {
    MITEK_VS_FLINKS_HOLDER: 'ID_VS_BANK_HOLDER',
    MITEK_VS_FLINKS_ADDRESS: 'ID_VS_BANK_ADDRESS',
    OTP_VS_FLINKS_EMAIL: 'OTP_VS_BANK_EMAIL',
    OTP_VS_FLINKS_PHONE: 'OTP_VS_BANK_PHONE',
    MITEK_DEDUP: 'ID_DEDUP',
    MITEK_VS_BUREAU_NAME: 'ID_VS_BUREAU_NAME',
    MITEK_VS_BUREAU_DOB: 'ID_VS_BUREAU_DOB',
    MITEK_VS_BUREAU_ADDRESS: 'ID_VS_BUREAU_ADDRESS',
  };
  return aliases[mt] || mt;
}

const STATUS_RANK: Record<string, number> = {
  FAIL: 0,
  ERROR: 1,
  REVIEW: 2,
  PASS_WITH_VARIATION: 3,
  PASS: 4,
  SKIPPED: 5,
  UNAVAILABLE: 6,
};

/** Field-centric Cross-checks: one row per field, sources as columns. */
/**
 * `authoritative` names the match type that ALREADY answers the whole field, so the
 * row shows that verdict instead of a worst-of vote across source pairs. The agent
 * resolves the legal name once across every source (IDENTITY_NAME) and decides on
 * that one verdict; merging the pairwise rows here could show a status the engine
 * never acted on. The pairwise rows still populate the per-source columns.
 */
const FIELD_SPECS: Array<{ label: string; matchTypes: string[]; authoritative?: string }> = [
  {
    label: 'Legal name',
    authoritative: 'IDENTITY_NAME',
    matchTypes: [
      'IDENTITY_NAME',
      'ID_VS_BANK_HOLDER',
      'MITEK_VS_FLINKS_HOLDER',
      'ID_VS_BUREAU_NAME',
      'MITEK_VS_BUREAU_NAME',
    ],
  },
  {
    label: 'Address',
    matchTypes: [
      'ID_VS_BANK_ADDRESS',
      'MITEK_VS_FLINKS_ADDRESS',
      'ID_VS_BUREAU_ADDRESS',
      'MITEK_VS_BUREAU_ADDRESS',
    ],
  },
  { label: 'Email', matchTypes: ['OTP_VS_BANK_EMAIL', 'OTP_VS_FLINKS_EMAIL'] },
  { label: 'Phone', matchTypes: ['OTP_VS_BANK_PHONE', 'OTP_VS_FLINKS_PHONE'] },
  { label: 'Date of birth', matchTypes: ['ID_VS_BUREAU_DOB', 'MITEK_VS_BUREAU_DOB'] },
  { label: 'Employer', matchTypes: ['PAYOR_VS_EMPLOYER'] },
];

/**
 * Display-only: when DE/Bureau is missing but a sibling source match already
 * passed (or identity is present with only bureau empty), show PASS.
 * Does not change STP / wire status from the agent.
 */
export function softenCrossCheckStatus(
  row: Pick<LoanReviewCrossCheckRow, 'matchType' | 'status' | 'reasonCodes' | 'identity'>,
  allRows: Array<Pick<LoanReviewCrossCheckRow, 'matchType' | 'status'>>,
): string {
  const status = (row.status || '').toUpperCase();
  if (status !== 'UNAVAILABLE') return row.status;

  const mt = row.matchType.toUpperCase();
  const nmt = normalizeMatchType(mt);
  if (!nmt.startsWith('ID_VS_BUREAU_') && !mt.startsWith('MITEK_VS_BUREAU_')) return row.status;

  const siblingType = BUREAU_SIBLING_MATCH[mt] || BUREAU_SIBLING_MATCH[nmt];
  if (siblingType) {
    const siblingWant = normalizeMatchType(siblingType);
    const sibling = allRows.find(
      (r) =>
        r.matchType.toUpperCase() === siblingType ||
        normalizeMatchType(r.matchType) === siblingWant,
    );
    if (
      sibling &&
      ['PASS', 'PASS_WITH_VARIATION'].includes((sibling.status || '').toUpperCase())
    ) {
      return 'PASS';
    }
  }

  if (row.identity) {
    return 'PASS';
  }

  return row.status;
}

function firstFilled(...values: Array<string | null | undefined>): string | null {
  for (const v of values) {
    if (v) return v;
  }
  return null;
}

function pickWorstStatus(statuses: string[]): string {
  if (statuses.length === 0) return 'UNAVAILABLE';
  let best = statuses[0];
  let bestRank = STATUS_RANK[(best || '').toUpperCase()] ?? 99;
  for (const raw of statuses.slice(1)) {
    const s = (raw || '').toUpperCase();
    const rank = STATUS_RANK[s] ?? 99;
    if (rank < bestRank) {
      best = raw;
      bestRank = rank;
    }
  }
  return best;
}

function normalizeCrossCheckValue(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Merge pairwise cross-checks into one row per field (Legal name, DOB, …).
 * Status ignores pure UNAVAILABLE bureau when a sibling match already decided.
 * Soften all-UNAVAILABLE → PASS only when the field’s primary source is present
 * (Identity for ID fields; income/employer for Employer) — never because a
 * secondary column alone has a value while Identity is missing.
 */
export function pivotCrossChecksToFieldRows(
  pairwise: LoanReviewCrossCheckRow[],
): LoanReviewCrossCheckRow[] {
  const byType = new Map(
    pairwise.map((r) => [r.matchType.toUpperCase(), r] as const),
  );
  const used = new Set<string>();
  const rows: LoanReviewCrossCheckRow[] = [];

  for (const spec of FIELD_SPECS) {
    const parts = spec.matchTypes
      .map((t) => byType.get(t))
      .filter((r): r is LoanReviewCrossCheckRow => Boolean(r));
    if (parts.length === 0) continue;
    for (const t of spec.matchTypes) used.add(t);

    const identity = firstFilled(...parts.map((p) => p.identity));
    const income = firstFilled(...parts.map((p) => p.income));
    const employer = firstFilled(...parts.map((p) => p.employer));
    const bureau = firstFilled(...parts.map((p) => p.bureau));
    // A merged field row asked every source its parts asked. Legal name asks the
    // bureau even when only the bank answered, so an empty Bureau column on that row
    // is a source that did not answer, not a column that never applied.
    const asked = [...new Set(parts.flatMap((p) => p.asked ?? []))];

    // One verdict already covers the field: show it, do not re-derive one.
    const decided = spec.authoritative
      ? parts.find((p) => normalizeMatchType(p.matchType) === spec.authoritative)
      : undefined;
    if (decided) {
      rows.push({
        matchType: spec.authoritative as string,
        compare: spec.label,
        identity,
        income,
        employer,
        bureau,
        asked,
        status: decided.status,
        reasonCodes: decided.reasonCodes,
      });
      continue;
    }

    const reasonCodes = [...new Set(parts.flatMap((p) => p.reasonCodes))];

    const actionable = parts
      .map((p) => p.status)
      .filter((s) => {
        const u = (s || '').toUpperCase();
        return u && u !== 'UNAVAILABLE';
      });

    let status: string;
    if (actionable.length > 0) {
      status = pickWorstStatus(actionable);
    } else {
      // All pairwise UNAVAILABLE. Conflicting secondary sources with no ID → review.
      const sideValues = [income, employer, bureau].filter((v): v is string => Boolean(v));
      const distinctSides = new Set(sideValues.map(normalizeCrossCheckValue));
      if (!identity && spec.label !== 'Employer' && distinctSides.size > 1) {
        status = 'REVIEW';
      } else {
        // Soften only when the field’s primary/anchor source is present.
        const primaryPresent =
          spec.label === 'Employer' ? Boolean(income || employer) : Boolean(identity);
        status = primaryPresent
          ? 'PASS'
          : pickWorstStatus(parts.map((p) => p.status));
      }
    }

    rows.push({
      matchType: spec.matchTypes.join('+'),
      compare: spec.label,
      identity,
      income,
      employer,
      bureau,
      asked,
      status,
      reasonCodes,
    });
  }

  for (const p of pairwise) {
    if (used.has(p.matchType.toUpperCase())) continue;
    rows.push(p);
  }

  return rows;
}

function softVariationMessage(raw: string): string {
  return raw
    .replace(/\s*—\s*variation noted/gi, ' — soft difference')
    .replace(/passed with variation/gi, 'soft difference')
    .replace(/mismatch noted/gi, 'soft difference');
}

/** Rollup gate titles that duplicate field-level soft diffs in Attention. */
function isRedundantRollupVariationMessage(msg: string): boolean {
  return (
    /^Bank account holder matches ID\b/i.test(msg) ||
    /^Pre-decision engine rollup\b/i.test(msg) ||
    /^Final STP\b/i.test(msg)
  );
}

export type CrossCheckColumn = 'identity' | 'income' | 'employer' | 'bureau';

/**
 * Which two columns a comparison actually involves.
 *
 * This is the single place that knows it, and both the values and the "which sources
 * were asked" set are built from it — so a match type added here cannot end up with its
 * value in one column and its participation recorded in another.
 */
export function crossCheckColumnsFor(
  matchType: string,
): { left: CrossCheckColumn; right: CrossCheckColumn } | null {
  const mt = normalizeMatchType(matchType);
  if (mt === 'ID_DEDUP') return null;

  // Consolidated legal-name verdict: ID name on the left, the bureau spelling on
  // the right. The bank column is filled from the pairwise holder row alongside it.
  if (mt === 'IDENTITY_NAME') return { left: 'identity', right: 'bureau' };

  if (
    mt === 'ID_VS_BANK_HOLDER' ||
    mt === 'ID_VS_BANK_ADDRESS' ||
    mt === 'OTP_VS_BANK_EMAIL' ||
    mt === 'OTP_VS_BANK_PHONE'
  ) {
    return { left: 'identity', right: 'income' };
  }
  if (mt.startsWith('ID_VS_BUREAU_')) return { left: 'identity', right: 'bureau' };
  if (mt === 'PAYOR_VS_EMPLOYER') return { left: 'income', right: 'employer' };

  // Unknown pairwise: keep both values visible under Identity / Income as a fallback.
  return { left: 'identity', right: 'income' };
}

/**
 * Place API left/right values into Identity / Income / Employer / Bureau columns.
 * Returns null for single-source checks that should not appear in the pairwise table.
 */
export function mapCrossCheckSources(
  matchType: string,
  left: string | null,
  right: string | null,
): Pick<LoanReviewCrossCheckRow, 'identity' | 'income' | 'employer' | 'bureau'> | null {
  const cols = crossCheckColumnsFor(matchType);
  if (!cols) return null;
  const out = { identity: null, income: null, employer: null, bureau: null } as Pick<
    LoanReviewCrossCheckRow,
    'identity' | 'income' | 'employer' | 'bureau'
  >;
  out[cols.left] = left;
  out[cols.right] = right;
  return out;
}

const SUB_CHECK_LABELS: Record<string, string> = {
  doctrine: 'Doctrine status',
  payor_match: 'Payor matches employer',
  existence: 'Employer exists',
  linkage: 'Applicant linked to employer',
  legitimacy: 'Looks like a real workplace',
  temporal: 'Employment timing',
  structural: 'Job/employer consistency',
  attempts: 'Verification attempts',
  fraud: 'Fraud signals',
  confidence: 'Confidence score',
};

const SUB_CHECK_ACTIONS: Record<string, string> = {
  doctrine: 'Checked the employer verification doctrine outcome',
  payor_match: 'Compared the bank payor name against the employer name',
  existence: 'Confirmed the employer exists',
  linkage: 'Confirmed the applicant is linked to the employer',
  legitimacy: 'Checked whether the employer looks like a real, trustworthy workplace',
  temporal: 'Checked employment timing and tenure',
  structural: 'Checked job title, industry, and work setup consistency',
  attempts: 'Checked the number of verification attempts',
  fraud: 'Checked for employer fraud or risk signals',
  confidence: 'Checked overall confidence in the employer verification',
};

/** Gate-level (non-employer) row text — employer uses its own sub-checks instead. */
const GATE_ROW_META: Record<number, { field: string; action: string }> = {
  1: { field: 'Identity verification', action: 'Verified ID document, liveness, and face match' },
  2: { field: 'Bank ownership', action: 'Verified the bank account is owned by the applicant' },
  3: { field: 'Income verification', action: 'Verified income amount, stability, and recurrence' },
  6: { field: 'Bureau identity', action: 'Checked bureau hit status, fraud alerts, and identity match' },
  7: { field: 'Credit decision', action: 'Confirmed a credit decision was returned' },
  8: { field: 'Internal collections history', action: 'Checked internal NSF / collections alerts' },
};

function gateNumber(gateId: string): number | null {
  const m = /^G0*(\d+)/.exec((gateId || '').toUpperCase());
  return m ? Number(m[1]) : null;
}

/** Which service card a gate belongs to. Orchestrator rollups (G00/G05/G09/G10) get no table. */
function gateServiceKey(gateId: string): LoanReviewServiceKey | 'orchestrator' | null {
  const n = gateNumber(gateId);
  if (n == null) return null;
  if (n === 1) return 'identity';
  if (n === 2 || n === 3) return 'income';
  if (n === 4) return 'employer';
  if (n === 6 || n === 7 || n === 8) return 'bureau';
  if (n === 11) return 'cross_check';
  if (n === 0 || n === 5 || n === 9 || n === 10) return 'orchestrator';
  return null;
}

function receivedTextFromCodes(codes: string[], fallback: string): string {
  return codes.length ? codes.map((c) => plainReason(c)).join('; ') : fallback;
}

const SOURCE_CARD_FOR_SERVICE: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, string> = {
  identity: 'identity',
  income: 'income',
  employer: 'employer',
  bureau: 'decision_engine',
};

function crossCheckHint(row: LoanReviewCrossCheckRow): string | null {
  const parts: string[] = [];
  if (row.identity) parts.push(`ID: ${row.identity}`);
  if (row.income) parts.push(`Bank/Income: ${row.income}`);
  if (row.employer) parts.push(`Employer: ${row.employer}`);
  if (row.bureau) parts.push(`Bureau: ${row.bureau}`);
  return parts.length ? parts.join('  ·  ') : null;
}

/** Short pill-tooltip line — field + match/soft only (no reason-code jargon). */
export function crossCheckTooltipLine(row: LoanReviewCrossCheckRow): string | null {
  const st = (row.status || '').toUpperCase();
  const field = (row.compare || '').trim() || 'Field';
  if (st === 'PASS_WITH_VARIATION') return `${field} has a soft difference`;
  if (st === 'FAIL' || st === 'REVIEW') return `${field} does not match`;
  return null;
}

function noteTargetFromText(text: string): {
  targetField: string | null;
  appTab: ReviewNoteAppTab | null;
  incomeSubTarget: IncomeSubTarget | null;
} {
  const t = text.toLowerCase();
  if (/gambling/.test(t)) {
    return { targetField: null, appTab: 'income', incomeSubTarget: 'gambling-debits' };
  }
  // Backend reason bullets end "(see Identity tab)" / "(see Income tab)" / "(see
  // Employer tab)" — the card the reason came from. That pointer is authoritative.
  if (/see identity tab/.test(t)) return { targetField: null, appTab: 'identity', incomeSubTarget: null };
  // Employer / income gate issues → real admin tabs (not the cross-check matrix).
  // Prefer "(see Employer tab)" / employer gate copy over any cross-check field match.
  if (
    /see\s+employer\s+tab|employer exists|trustworthy workplace|work setup|employment details|employer verification|employer fraud|employer timing|link .+ employer|link the applicant to|consistent with the employer/.test(
      t,
    )
  ) {
    return { targetField: null, appTab: 'employer', incomeSubTarget: null };
  }
  // Payday stacking has no sub-view of its own; the loans sit in the Income tab's
  // bank activity, so the note opens that tab.
  if (/payday loans? (are|is) being serviced|payday stacking|existing loan obligations/.test(t)) {
    return { targetField: null, appTab: 'income', incomeSubTarget: null };
  }
  if (/see income tab|income verification|income recurrence|income stability|income volatility|income classification/.test(t)) {
    return { targetField: null, appTab: 'income', incomeSubTarget: null };
  }
  // Legal name before address / date of birth: "a different surname, confirmed by a
  // matching date of birth" is a NAME finding that happens to mention the DOB. And no
  // catch-all "soft difference from the ID" here — "bureau address has a soft
  // difference from the ID" is an address finding and used to land on Legal name.
  if (
    /legal name|surname|holder name|bank account holder|bureau name|^id name|[ (]id name|name held by|name to confirm|name does not match|name tokens match|middle initial/.test(
      t,
    )
  ) {
    return { targetField: 'Legal name', appTab: null, incomeSubTarget: null };
  }
  if (/address/.test(t)) return { targetField: 'Address', appTab: null, incomeSubTarget: null };
  if (/date of birth|dob/.test(t)) return { targetField: 'Date of birth', appTab: null, incomeSubTarget: null };
  if (/email/.test(t)) return { targetField: 'Email', appTab: null, incomeSubTarget: null };
  if (/phone/.test(t)) return { targetField: 'Phone', appTab: null, incomeSubTarget: null };
  if (/payor|payroll source vs employer/.test(t)) return { targetField: 'Employer', appTab: null, incomeSubTarget: null };
  return { targetField: null, appTab: null, incomeSubTarget: null };
}

/**
 * Rewrite source-less soft name lines using cross-check evidence.
 * Older runs said "name differs by middle initial only" even when it was bureau.
 */
export function clarifySoftNameNote(
  text: string,
  crossChecks: LoanReviewCrossCheckRow[],
): string {
  const trimmed = text.trim();
  if (/bureau name|bank holder name/i.test(trimmed)) return text;

  const isMiddle = /middle initial/i.test(trimmed) && /name/i.test(trimmed);
  const isToken = /name tokens match|name order/i.test(trimmed);
  if (!isMiddle && !isToken) return text;

  const legal = crossChecks.find((r) => r.compare.toLowerCase() === 'legal name');
  const codes = (legal?.reasonCodes ?? []).map((c) => String(c).toUpperCase());
  const bankHard = codes.includes('BANK_HOLDER_IDENTITY_MISMATCH');
  const bureauSoft =
    codes.includes('BUREAU_NAME_VARIATION') ||
    Boolean(legal?.bureau && (bankHard || codes.some((c) => c.startsWith('NAME_'))));

  if (bureauSoft || (legal?.bureau && bankHard)) {
    return 'Bureau name has a soft difference from the ID';
  }
  return 'Bank holder name has a soft difference from the ID';
}

/** Drop OTP jargon from soft contact notes (older published copy). */
export function clarifyContactSoftNote(text: string): string {
  return text
    .replace(/application\s*\/\s*OTP\s*phone/gi, 'the registered phone number')
    .replace(/application\s*\/\s*OTP\s*email/gi, 'the registered email')
    .replace(/\bOTP\s*phone\b/gi, 'the registered phone number')
    .replace(/\bOTP\s*email\b/gi, 'the registered email');
}

/**
 * Where a click on a note goes, as published by the loan-review agent
 * (`noteRefs`, keyed by note text): the reason codes behind it and the application
 * tab of the card it came from.
 */
export type NoteRef = {
  codes: string[];
  tab: ReviewNoteAppTab | null;
  incomeSubTarget: IncomeSubTarget | null;
};

export function parseNoteRefs(raw: unknown): Record<string, NoteRef> {
  const out: Record<string, NoteRef> = {};
  const rec = asRecord(raw);
  if (!rec) return out;
  for (const [text, v] of Object.entries(rec)) {
    const r = asRecord(v);
    if (!r) continue;
    const tab = str(r.tab);
    const sub = str(r.incomeSubTarget);
    out[text.trim()] = {
      codes: asArray(r.codes).map((c) => String(c).toUpperCase()).filter(Boolean),
      tab: tab === 'identity' || tab === 'income' || tab === 'employer' ? tab : null,
      incomeSubTarget: sub === 'gambling-debits' ? sub : null,
    };
  }
  return out;
}

/**
 * The target from a published reference — structural, so it survives any rewording.
 * Precedence: the income sub-view (gambling), then the cross-check row whose own
 * reason codes include one of the note's codes, then the card's tab. Null when the
 * reference gives nothing to go on, and the caller falls back to the wording.
 */
function noteTargetFromRef(
  ref: NoteRef | undefined,
  crossChecks: LoanReviewCrossCheckRow[],
): { targetField: string | null; appTab: ReviewNoteAppTab | null; incomeSubTarget: IncomeSubTarget | null } | null {
  if (!ref) return null;
  if (ref.incomeSubTarget) return { targetField: null, appTab: 'income', incomeSubTarget: ref.incomeSubTarget };
  if (ref.codes.length) {
    const row = crossChecks.find((r) => (r.reasonCodes ?? []).some((c) => ref.codes.includes(String(c).toUpperCase())));
    if (row) return { targetField: row.compare, appTab: null, incomeSubTarget: null };
  }
  if (ref.tab) return { targetField: null, appTab: ref.tab, incomeSubTarget: null };
  return null;
}

/** Attach hover evidence + jump target (app tab or cross-check matrix). */
export function enrichReasonNotes(
  notes: string[],
  crossChecks: LoanReviewCrossCheckRow[],
  refs: Record<string, NoteRef> = {},
): ReviewReasonNote[] {
  return notes.map((raw) => {
    const text = clarifyContactSoftNote(clarifySoftNameNote(raw, crossChecks));
    // Published reference first; the wording is only a fallback for runs that
    // predate references.
    const { targetField, appTab, incomeSubTarget } =
      noteTargetFromRef(refs[raw.trim()] ?? refs[text.trim()], crossChecks) ?? noteTargetFromText(text);
    const row = targetField
      ? crossChecks.find((r) => r.compare.toLowerCase() === targetField.toLowerCase())
      : undefined;
    const hint = row ? crossCheckHint(row) : null;
    return {
      text,
      hint,
      targetField: targetField ?? null,
      appTab: appTab ?? null,
      incomeSubTarget: incomeSubTarget ?? null,
    };
  });
}

/** Groups gates into per-service hero pills. Domain status ignores cross-check bleed. */
function buildServiceSections(
  gateRows: LoanReviewGateRow[],
  crossChecks: LoanReviewCrossCheckRow[],
  agent1: LoanReviewAgentSlice,
  sourceCards: SourceCardViewModel[],
): LoanReviewServiceSection[] {
  const domainKeys: Array<Exclude<LoanReviewServiceKey, 'cross_check'>> = [
    'identity',
    'income',
    'employer',
    'bureau',
  ];
  const rowsByService: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, LoanReviewServiceRow[]> = {
    identity: [],
    income: [],
    employer: [],
    bureau: [],
  };
  const domainStatusRows: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, string[]> = {
    identity: [],
    income: [],
    employer: [],
    bureau: [],
  };
  const domainReasonTips: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, string[]> = {
    identity: [],
    income: [],
    employer: [],
    bureau: [],
  };

  for (const g of gateRows) {
    const key = gateServiceKey(g.gateId);
    if (!key || key === 'orchestrator' || key === 'cross_check') continue;

    if (key === 'employer') {
      const visibleSubChecks = g.subChecks.filter(
        (sc) => !['SKIP', 'SKIPPED'].includes(sc.status.toUpperCase()),
      );
      if (visibleSubChecks.length > 0) {
        for (const sc of visibleSubChecks) {
          rowsByService.employer.push({
            field: sc.label,
            received: receivedTextFromCodes(sc.reasonCodes, 'No issues reported'),
            action: SUB_CHECK_ACTIONS[sc.name] ?? 'Checked an employer verification signal',
            status: sc.status,
          });
          domainStatusRows.employer.push(sc.status);
          if (['FAIL', 'REVIEW', 'ERROR'].includes(sc.status.toUpperCase())) {
            for (const c of sc.reasonCodes) domainReasonTips.employer.push(plainReason(c));
          }
        }
        continue;
      }
    }

    const meta = GATE_ROW_META[gateNumber(g.gateId) ?? -1];
    if (!meta) continue;
    rowsByService[key].push({
      field: meta.field,
      received: receivedTextFromCodes(g.reasonCodes, 'No issues reported'),
      action: meta.action,
      status: g.status,
    });
    domainStatusRows[key].push(g.status);
    if (['FAIL', 'REVIEW', 'ERROR'].includes(g.status.toUpperCase())) {
      for (const c of g.reasonCodes) domainReasonTips[key].push(plainReason(c));
    }
  }

  // Keep compared values on service tables for legacy view — do NOT fold into overallStatus.
  const crossCheckColumn: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, 'identity' | 'income' | 'employer' | 'bureau'> = {
    identity: 'identity',
    income: 'income',
    employer: 'employer',
    bureau: 'bureau',
  };
  for (const key of domainKeys) {
    const column = crossCheckColumn[key];
    for (const row of crossChecks) {
      const value = row[column];
      if (!value) continue;
      rowsByService[key].push({
        field: row.compare,
        received: value,
        action: 'Compared against the other verified sources',
        status: row.status,
      });
    }
  }

  const agentLineLabel: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, string | null> = {
    identity: 'Identity',
    income: 'Income',
    employer: 'Employer',
    bureau: null,
  };

  const titles: Record<Exclude<LoanReviewServiceKey, 'cross_check'>, string> = {
    identity: 'Identity (MiTek)',
    income: 'Income & bank',
    employer: 'Employer',
    bureau: 'DE / Bureau (TransUnion)',
  };

  const sections: LoanReviewServiceSection[] = domainKeys.map((key) => {
    const rows = rowsByService[key];
    const card = sourceCards.find((c) => c.key === SOURCE_CARD_FOR_SERVICE[key]);
    const overallStatus = card?.status
      ?? (domainStatusRows[key].length
        ? pickWorstStatus(domainStatusRows[key])
        : rows.length
          ? 'UNAVAILABLE'
          : 'UNAVAILABLE');
    const tips = [...new Set(domainReasonTips[key])];
    if (card?.reasonCodes?.length) {
      for (const c of card.reasonCodes) tips.push(plainReason(c));
    }
    const agentLabel = agentLineLabel[key];
    const agentLine = agentLabel ? agent1.lines.find((l) => l.label === agentLabel) ?? null : null;
    return {
      key,
      title: titles[key],
      overallStatus,
      rows,
      agentLine,
      tooltipReasons: [...new Set(tips)].slice(0, 6),
    };
  });

  const crossCard = sourceCards.find((c) => c.key === 'cross_check');
  const g11 = gateRows.find((g) => gateNumber(g.gateId) === 11);
  const crossStatus =
    crossCard?.status
    ?? (g11?.status)
    ?? (crossChecks.length ? pickWorstStatus(crossChecks.map((r) => r.status)) : null);
  if (crossStatus) {
    // Keep pill tooltips short for operators — no middle-initial / variation jargon.
    const crossTips: string[] = [];
    for (const row of crossChecks) {
      const tip = crossCheckTooltipLine(row);
      if (tip) crossTips.push(tip);
    }
    sections.push({
      key: 'cross_check',
      title: 'Cross-check',
      overallStatus: crossStatus,
      rows: crossChecks.map((row) => ({
        field: row.compare,
        received: crossCheckHint(row) ?? '—',
        action: 'Compared across identity sources',
        status: row.status,
      })),
      agentLine: null,
      tooltipReasons: [...new Set(crossTips)].slice(0, 8),
    });
  }

  return sections.filter((s) => s.rows.length > 0 || s.key === 'cross_check');
}

const STATUS_CHIP_LABEL: Record<string, string> = {
  identity: 'Identity',
  income: 'Income',
  employer: 'Employer',
  decision_engine: 'Decision engine',
  bureau: 'Bureau',
  cross_check: 'Cross-source',
};

/** Fixed left-to-right order so the chip row reads the same on every application. */
const STATUS_CHIP_ORDER = ['identity', 'income', 'employer', 'decision_engine', 'bureau', 'cross_check'];

/**
 * Hero chips read the same status the source cards read, so the two can never
 * disagree. Cross-source consistency gets its own chip instead of being folded into
 * every source it touches: doing that made one mismatch (e.g. bank holder vs ID)
 * render Identity, Income *and* Bureau as "Fail" while each card showed Pass 100 and
 * loan review had approved the file.
 *
 * Older runs have no source cards, so fall back to the legacy service sections —
 * there is no card to contradict, and those sections do list the cross-check rows.
 */
function buildServiceChips(
  sourceCards: SourceCardViewModel[],
  services: LoanReviewServiceSection[],
): LoanReviewStatusChip[] {
  if (sourceCards.length > 0) {
    return sourceCards
      .map((card) => ({
        key: card.key,
        label: STATUS_CHIP_LABEL[card.key] ?? (card.signal || card.key.replace(/_/g, ' ')),
        status: card.status,
      }))
      .sort((a, b) => {
        const ai = STATUS_CHIP_ORDER.indexOf(a.key);
        const bi = STATUS_CHIP_ORDER.indexOf(b.key);
        return (ai === -1 ? STATUS_CHIP_ORDER.length : ai) - (bi === -1 ? STATUS_CHIP_ORDER.length : bi);
      });
  }

  // No source cards: fall back to service sections, and keep standard domains
  // present with empty status so the hero can show "No data" instead of omitting them.
  // Keys are strings: STATUS_CHIP_ORDER mixes service keys with source-card keys
  // (e.g. decision_engine) that are not on LoanReviewServiceKey.
  const fromServices = new Map<string, LoanReviewStatusChip>(
    services.map((section) => [
      section.key,
      {
        key: section.key,
        label: STATUS_CHIP_LABEL[section.key] ?? section.key,
        status: section.rows.length ? section.overallStatus : '',
      } satisfies LoanReviewStatusChip,
    ]),
  );
  return STATUS_CHIP_ORDER.filter((key) => key !== 'decision_engine' || fromServices.has(key)).map(
    (key) =>
      fromServices.get(key) ?? {
        key,
        label: STATUS_CHIP_LABEL[key] ?? key,
        status: '',
      },
  );
}

/** Plain-language pipeline/STP context for the orchestrator card — no raw IDs. */
function buildOrchestratorSummary(opts: {
  gateRows: LoanReviewGateRow[];
  agentsPresent: boolean[];
  processingTimeMs: number | null;
  wouldBeStp: boolean | null;
  stpKillSwitch: string | null;
}): string[] {
  const lines: string[] = [];
  const presentCount = opts.agentsPresent.filter(Boolean).length;
  const timeText = opts.processingTimeMs != null ? `${opts.processingTimeMs}ms` : 'an unknown time';
  lines.push(
    `Ran ${opts.gateRows.length} gate${opts.gateRows.length === 1 ? '' : 's'} and ${presentCount} of 4 review agents in ${timeText}.`,
  );
  if (opts.wouldBeStp != null || opts.stpKillSwitch) {
    const stpText =
      opts.wouldBeStp == null
        ? 'unknown'
        : opts.wouldBeStp
          ? 'would qualify for straight-through processing'
          : 'would not qualify for straight-through processing';
    lines.push(
      `Auto-approval eligibility: ${stpText}${opts.stpKillSwitch ? ` (kill switch: ${opts.stpKillSwitch})` : ''}.`,
    );
  }
  for (const g of opts.gateRows) {
    if (gateServiceKey(g.gateId) !== 'orchestrator') continue;
    if (['PASS', 'SKIPPED'].includes(g.status.toUpperCase())) continue;
    const why = g.reasonCodes[0] ? plainReason(g.reasonCodes[0]) : g.status;
    lines.push(`${g.title}: ${why}`);
  }
  return lines;
}

function stageFromAgent(
  present: boolean,
  soft: boolean,
  blocked: boolean,
  subtitle: string,
): { status: PipelineStageStatus; subtitle: string } {
  if (!present) return { status: 'missing', subtitle: subtitle || 'Not available for this run' };
  if (blocked) return { status: 'blocked', subtitle };
  if (soft) return { status: 'soft', subtitle };
  return { status: 'done', subtitle };
}

// ── Source cards (per-source received → analysis → status) ──────────────────

const RECEIVED_FIELD_LABELS: Record<string, string> = {
  legal_name: 'Legal name',
  dob: 'Date of birth',
  address: 'Address',
  document_type: 'ID document',
  verification_status: 'ID verification',
  province: 'Province',
  confidence: 'Confidence',
  monthly_amount: 'Monthly income',
  income_class: 'Income class',
  verdict: 'Income verdict',
  stability_score: 'Income stability',
  primary_payor: 'Pays from',
  employer_name: 'Employer',
  doctrine_status: 'Employer verification',
  sector: 'Sector',
  provenir_decision: 'Credit decision',
  requested_amount: 'Requested amount',
  approved_amount: 'Approved amount',
  offer_min_amount: 'Offer minimum',
  offer_max_amount: 'Offer maximum',
  credit_score: 'Credit score',
  bureau_hit: 'Bureau match',
  bureau_name: 'Bureau name',
  bureau_dob: 'Bureau date of birth',
};

const MONEY_FIELDS = new Set([
  'monthly_amount',
  'requested_amount',
  'approved_amount',
  'offer_min_amount',
  'offer_max_amount',
]);

/** Friendly labels for every sub-check name across all gates. */
const ALL_SUB_CHECK_LABELS: Record<string, string> = {
  ...SUB_CHECK_LABELS,
  verification: 'ID verification',
  forensic: 'Document forensics',
  entity: 'Entity match',
  dedup: 'Duplicate check',
  liveness: 'Liveness',
  face_match: 'Face match',
  document: 'ID document',
  province: 'Province eligibility',
  verdict: 'Income verdict',
  arbitration: 'Pay cycles',
  recurrence: 'Recurring deposits',
  stability: 'Income stability',
  amount: 'Income amount',
  income_class: 'Income class',
  volatility: 'Income volatility',
  hit: 'Bureau match',
  credit: 'Credit decision',
  name: 'Name',
  // One verdict across every name source. holder_name / bureau_name are retired but
  // kept so stored snapshots predating the consolidation still render a label.
  legal_name: 'Legal name across sources',
  holder_name: 'Bank holder name',
  ibv: 'Bank verification',
  bureau_name: 'Bureau name',
  dob: 'Date of birth',
  address: 'Address',
  email: 'Email',
  phone: 'Phone',
  payor: 'Payor matches employer',
  EMPLOYER_LOOKUP: 'Employer lookup',
  REGISTRY_STATUS: 'Employer registry status',
  ADDRESS_VERIFICATION: 'Address verification',
  PHONE_VERIFICATION: 'Phone verification',
  DIRECT_PAYOR_MATCH: 'Direct payor match',
  PAYROLL_PROVIDER_MATCH: 'Payroll provider match',
  PERSONAL_TRANSFER_DETECTION: 'Personal transfer check',
  OWNER_OPERATOR_MATCH: 'Owner-operator match',
  UNKNOWN_PAYOR: 'Unrecognised payor',
  GOVERNMENT_SCHEME_PAYOR: 'Government benefit payor',
  AI_LINKAGE_ANALYSIS: 'AI payroll check',
};

/**
 * Sub-check / score-row label. LRA sends employer checkpoint names in lower case
 * on sub_checks ("direct_payor_match") and upper case on score_breakdown
 * ("DIRECT_PAYOR_MATCH"), so look up both before falling back to the raw name.
 */
function subCheckLabel(key: string): string {
  if (!key) return 'Check';
  return (
    ALL_SUB_CHECK_LABELS[key] ??
    ALL_SUB_CHECK_LABELS[key.toUpperCase()] ??
    ALL_SUB_CHECK_LABELS[key.toLowerCase()] ??
    key.replace(/_/g, ' ')
  );
}

const DOMAIN_LABELS: Record<string, string> = {
  identity: 'Identity',
  income: 'Income',
  employment: 'Employer',
  bureau_credit: 'Decision engine',
  external: 'External context',
  cross_check: 'Cross-check',
  internal_ca: 'Internal history',
};

function formatReceivedValue(key: string, value: unknown): string {
  if (MONEY_FIELDS.has(key)) {
    const n = num(value);
    if (n != null) return `$${Math.round(n).toLocaleString('en-CA')}`;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return str(value) ?? '';
}

/** "passed" / "needs a human look" / … for a sub-check status. */
function plainCheckResult(status: string): string {
  switch ((status || '').toUpperCase()) {
    case 'PASS':
      return 'passed';
    case 'PASS_WITH_VARIATION':
      return 'passed with a soft difference';
    case 'REVIEW':
      return 'needs a human look';
    case 'FAIL':
    case 'ERROR':
      return 'did not pass';
    case 'UNAVAILABLE':
      return 'could not be checked';
    default:
      return status || 'not run';
  }
}

function buildSubCheckPlain(label: string, status: string, reasonCodes: string[]): string {
  const base = `${label} ${plainCheckResult(status)}`;
  const s = (status || '').toUpperCase();
  if (['PASS', 'PASS_WITH_VARIATION', 'SKIP', 'SKIPPED'].includes(s) || !reasonCodes.length) {
    return base;
  }
  return `${base} — ${plainReason(reasonCodes[0])}`;
}

function buildCardSummary(signal: string, status: string, reasonCodes: string[]): string {
  const s = (status || '').toUpperCase();
  const why = reasonCodes[0] ? plainReason(reasonCodes[0]) : null;
  if (s === 'PASS') return `${signal} checks passed.`;
  if (s === 'PASS_WITH_VARIATION') return `${signal} passed with soft differences that did not block approval.`;
  if (s === 'REVIEW') return why ? `Sent to review — ${why.charAt(0).toLowerCase()}${why.slice(1)}.` : `${signal} needs a human look.`;
  if (s === 'FAIL' || s === 'ERROR') return why ? `Did not pass — ${why.charAt(0).toLowerCase()}${why.slice(1)}.` : `${signal} did not pass.`;
  if (s === 'UNAVAILABLE') return `Not enough data to check ${signal.toLowerCase()}.`;
  if (s === 'NOT_RUN') return `${signal} was not run for this application.`;
  return `${signal}: ${gateStatusDisplay(status)}.`;
}

/** Business-language narrative per check + status. Falls back to a generic line. */
interface CheckCopy {
  pass: string;
  soft?: string;
  review: string;
  fail: string;
  unavailable?: string;
}

const CHECK_COPY: Record<string, CheckCopy> = {
  verification: { pass: 'ID document verified', review: 'ID verification needs a look', fail: 'ID verification did not pass' },
  forensic: { pass: 'Document forensics passed', review: 'Document forensics need a look', fail: 'Document may be tampered' },
  entity: { pass: 'ID details are internally consistent', review: 'ID details need a look', fail: 'ID details did not match' },
  dedup: { pass: 'No duplicate identity found', review: 'Possible duplicate identity', fail: 'Duplicate identity detected' },
  liveness: { pass: 'Live person confirmed', review: 'Liveness was uncertain', fail: 'Liveness check failed' },
  face_match: { pass: 'Face matched the ID photo', review: 'Face match was uncertain', fail: 'Face did not match the ID' },
  document: { pass: 'ID document on file', review: 'ID document needs a look', unavailable: 'ID document type was not captured', fail: 'ID document missing' },
  province: { pass: 'Province is supported', review: 'Province needs a look', fail: 'Province is not supported' },
  attempts: { pass: 'Verification attempts within range', review: 'More verification retries than usual', fail: 'Too many verification attempts' },
  confidence: { pass: 'Match confidence was strong', review: 'Match confidence was low', fail: 'Match confidence was too low' },
  verdict: { pass: 'Income verification passed', review: 'Income verification needs review', fail: 'Income verification failed' },
  arbitration: { pass: 'Enough pay cycles seen', review: 'Pay cycles need a look', fail: 'Not enough pay cycles' },
  recurrence: { pass: 'Deposits recur reliably', review: 'Recurring deposits need a look', fail: 'Deposits do not recur reliably' },
  stability: { pass: 'Income was steady', review: 'Income stability needs a look', fail: 'Income was not stable' },
  amount: { pass: 'Income amount captured', review: 'Income amount needs a look', unavailable: 'Income amount was not available', fail: 'Income amount missing' },
  income_class: { pass: 'Income type is strong', soft: 'Income type is weaker than ideal', review: 'Income type needs a look', fail: 'Income type not acceptable' },
  volatility: { pass: 'Pay amounts were consistent', review: 'Pay amounts swung enough to review', fail: 'Pay amounts were too volatile' },
  doctrine: { pass: 'Employer verification cleared', review: 'Employer verification needs a look', fail: 'Employer verification did not clear' },
  existence: { pass: 'Employer exists', review: 'Employer existence needs a look', fail: 'Could not confirm the employer exists' },
  linkage: { pass: 'Applicant linked to employer', review: 'Employer link needs a look', fail: 'Could not link applicant to employer' },
  legitimacy: { pass: 'Employer looks legitimate', review: 'Employer legitimacy needs a look', fail: 'Employer may not be legitimate' },
  temporal: { pass: 'Employment timing checks out', review: 'Employment timing needs a look', fail: 'Employment timing did not check out' },
  structural: { pass: 'Role and employer are consistent', review: 'Role/employer consistency needs a look', fail: 'Role and employer are inconsistent' },
  fraud: { pass: 'No fraud signals', review: 'Fraud signals need a look', fail: 'Fraud signals were raised' },
  hit: { pass: 'Bureau found the applicant', review: 'Thin or no bureau file', fail: 'Bureau file issue' },
  credit: { pass: 'Credit decision approved', soft: 'Approved — requested more than offered', review: 'Credit decision needs a look', unavailable: 'Credit decision unavailable', fail: 'Credit decision not approved' },
  holder_name: { pass: 'Bank holder matches ID', review: 'Bank holder needs a look', fail: 'Bank holder does not match ID' },
  ibv: { pass: 'Bank account verified', review: 'Bank verification needs a look', fail: 'Bank verification failed' },
  bureau_name: { pass: 'Name matches the bureau', review: 'Name differs from the bureau', fail: 'Name does not match the bureau' },
  dob: { pass: 'Date of birth matches', review: 'Date of birth differs', fail: 'Date of birth does not match' },
  address: { pass: 'Address matches', soft: 'Address has a soft difference', review: 'Address needs a look', fail: 'Address does not match' },
  email: { pass: 'Email matches', soft: 'Email has a soft difference', review: 'Email needs a look', fail: 'Email does not match' },
  phone: { pass: 'Phone matches', soft: 'Phone has a soft difference', review: 'Phone needs a look', fail: 'Phone does not match' },
  payor: { pass: 'Payor matches the employer', review: 'Payor differs from the employer', fail: 'Payor does not match the employer' },
};

type SignalKind = 'money' | 'num' | 'text';

/** Numeric/text signals to reveal on hover, per check. First present key wins per row. */
const CHECK_SIGNALS: Record<string, Array<{ key: string; label: string; kind: SignalKind }>> = {
  confidence: [{ key: 'confidence', label: 'Confidence', kind: 'num' }],
  attempts: [
    { key: 'attempt_count', label: 'Attempts', kind: 'num' },
    { key: 'attempt_number', label: 'Attempts', kind: 'num' },
  ],
  stability: [{ key: 'stability_score', label: 'Stability score', kind: 'num' }],
  volatility: [
    { key: 'volatility_index', label: 'Volatility', kind: 'num' },
    { key: 'threshold', label: 'Limit', kind: 'num' },
  ],
  amount: [{ key: 'monthly_amount', label: 'Monthly income', kind: 'money' }],
  income_class: [{ key: 'income_class', label: 'Class', kind: 'text' }],
  verdict: [{ key: 'verdict', label: 'Verdict', kind: 'text' }],
};

function formatSignal(kind: SignalKind, value: unknown): string | null {
  if (kind === 'money') {
    const n = num(value);
    return n == null ? null : `$${Math.round(n).toLocaleString('en-CA')}`;
  }
  if (kind === 'num') {
    const n = num(value);
    if (n == null) return null;
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  }
  return str(value);
}

function buildSubCheckHint(name: string, raw: Record<string, unknown>): string | null {
  const specs = CHECK_SIGNALS[name];
  if (!specs) return null;
  const parts: string[] = [];
  for (const spec of specs) {
    const v = formatSignal(spec.kind, raw[spec.key]);
    if (v != null && v !== '') parts.push(`${spec.label}: ${v}`);
  }
  return parts.length ? parts.join('  ·  ') : null;
}

/** Rich narrative + hover hint for one sub-check. */
function subCheckNarrative(
  name: string,
  label: string,
  status: string,
  reasonCodes: string[],
  raw: Record<string, unknown>,
): { plain: string; hint: string | null } {
  const copy = CHECK_COPY[name];
  const s = (status || '').toUpperCase();
  let plain: string;
  if (copy) {
    if (s === 'PASS') plain = copy.pass;
    else if (s === 'PASS_WITH_VARIATION') plain = copy.soft ?? copy.pass;
    else if (s === 'REVIEW') plain = copy.review;
    else if (s === 'FAIL' || s === 'ERROR') plain = copy.fail;
    else if (s === 'UNAVAILABLE') plain = copy.unavailable ?? copy.review;
    else plain = buildSubCheckPlain(label, status, reasonCodes);
  } else {
    plain = buildSubCheckPlain(label, status, reasonCodes);
  }
  // Soft-difference checks carry the agent's explanation in `reason` (e.g. a name
  // check accepted on the AI payroll confirmation); the narrative alone only says
  // "soft difference", so surface it as the hint when there is no signal hint.
  const reason = typeof raw.reason === 'string' ? raw.reason.trim() : '';
  const hint = buildSubCheckHint(name, raw) ?? (s === 'PASS_WITH_VARIATION' && reason ? reason : null);
  return { plain, hint };
}

function parseSourceCard(raw: unknown): SourceCardViewModel | null {
  const c = asRecord(raw);
  if (!c) return null;
  const key = str(c.key);
  if (!key) return null;

  const receivedRec = asRecord(c.received) ?? {};
  const received = Object.entries(receivedRec)
    .map(([k, v]) => ({
      label: RECEIVED_FIELD_LABELS[k] ?? k.replace(/_/g, ' '),
      value: formatReceivedValue(k, v),
    }))
    .filter((r) => r.value);

  const analysis = asRecord(c.analysis) ?? {};
  const subChecks: SourceCardSubCheck[] = asArray(analysis.sub_checks)
    .map((sc) => {
      const s = asRecord(sc) ?? {};
      const name = str(s.name) ?? '';
      const status = str(s.status) ?? 'UNKNOWN';
      const reasonCodes = asArray(s.reason_codes).map(String);
      const label = subCheckLabel(name);
      const { plain, hint } = subCheckNarrative(name, label, status, reasonCodes, s);
      return {
        name,
        label,
        status,
        reasonCodes,
        plain,
        hint,
        field: str(s.field),
        idValue: str(s.id_value),
        otherValue: str(s.other_value),
        otherSource: str(s.other_source),
      };
    })
    .filter((sc) => !['SKIP', 'SKIPPED', ''].includes(sc.status.toUpperCase()));

  const scoreBreakdown: ScoreContribution[] = asArray(analysis.score_breakdown)
    .map((row) => {
      const r = asRecord(row) ?? {};
      const step = str(r.step) ?? '';
      return {
        label: subCheckLabel(step),
        status: str(r.status) ?? '',
        weight: num(r.weight) ?? 0,
        contribution: num(r.contribution) ?? 0,
      };
    })
    .filter((b) => b.weight > 0);

  const status = str(c.status) ?? 'UNKNOWN';
  const reasonCodes = asArray(c.reason_codes).map(String);
  const signal = str(c.signal) ?? key;

  return {
    key,
    source: str(c.source) ?? '',
    signal,
    gateId: str(c.gate_id) ?? '',
    status,
    blocksStp: Boolean(c.blocks_stp),
    // Prefer the capped contribution: a gate can score 100 internally and still
    // contribute 50 once its status caps it, and the card showing 100 next to a
    // chip showing 50 was the confusing part. Falls back for older payloads.
    weightedScore: num(analysis.contribution_score) ?? num(analysis.weighted_score),
    internalScore: num(analysis.weighted_score),
    capChecks: asArray(analysis.cap_checks).map((c) => String(c)).filter(Boolean),
    received,
    subChecks,
    reasonCodes,
    summary: buildCardSummary(signal, status, reasonCodes),
    scoreBreakdown,
  };
}

function parseCombinedScore(raw: Record<string, unknown> | null): CombinedScoreViewModel | null {
  if (!raw) return null;
  const domainRec = asRecord(raw.domain_scores) ?? {};
  // `card_score` is what the area scored on its own card; `score` is what the combined
  // score counted. A check that needs review is capped at its status score, so the two
  // differ — and the chip showing only the counted number is why the Employer card read
  // 82 while this chip read 50, with nothing on screen tying them together.
  const cardByDomain = new Map<string, { card: number | null; capped: boolean; status: string | null; capChecks: string[] }>();
  for (const row of asArray(raw.breakdown)) {
    const r = asRecord(row) ?? {};
    const domain = str(r.domain);
    if (!domain) continue;
    cardByDomain.set(domain, {
      card: num(r.card_score),
      capped: r.capped_by_status === true,
      status: str(r.status) ?? null,
      capChecks: asArray(r.cap_checks).map((c) => String(c)).filter(Boolean),
    });
  }
  const domainScores = Object.entries(domainRec)
    .map(([k, v]) => ({
      domain: k,
      label: DOMAIN_LABELS[k] ?? k.replace(/_/g, ' '),
      score: num(v) ?? 0,
      cardScore: cardByDomain.get(k)?.card ?? null,
      cappedByStatus: cardByDomain.get(k)?.capped ?? false,
      status: cardByDomain.get(k)?.status ?? null,
      capChecks: cardByDomain.get(k)?.capChecks ?? [],
    }))
    .filter((d) => Number.isFinite(d.score));
  const breakdown: CombinedDomainRow[] = asArray(raw.breakdown)
    .map((row) => {
      const r = asRecord(row) ?? {};
      const domain = str(r.domain) ?? '';
      return {
        domain,
        label: DOMAIN_LABELS[domain] ?? (domain ? domain.replace(/_/g, ' ') : 'Area'),
        status: str(r.status) ?? '',
        score: num(r.subscore) ?? 0,
        weight: num(r.weight) ?? 0,
        contribution: num(r.contribution) ?? 0,
      };
    })
    .filter((b) => b.weight > 0);
  const agent1Score = num(raw.agent1_score);
  const adjustments = mapAdjustments(raw.adjustments, raw);
  const topDrags = asArray(raw.top_drags)
    .map((row) => {
      const r = asRecord(row) ?? {};
      const domain = str(r.domain) ?? '';
      return { label: DOMAIN_LABELS[domain] ?? (domain ? domain.replace(/_/g, ' ') : 'Area'), pointsLost: num(r.points_lost) ?? 0 };
    })
    .filter((d) => d.pointsLost > 0);
  return {
    score: num(raw.score),
    band: str(raw.band),
    lane: str(raw.lane),
    status: str(raw.status),
    decisionBasis: str(raw.decision_basis),
    domainScores,
    breakdown,
    topDrags,
    agent1Score,
    adjustments,
  };
}

export function mapLoanReviewToViewModel(loanReview: unknown): LoanReviewViewModel {
  const lr = asRecord(loanReview);
  const emptyAgents = (): LoanReviewAgentSlice => ({ present: false, lines: [] });

  if (!lr) {
    return {
      outcomeLabel: 'None',
      requestStatus: null,
      decision: null,
      primaryReasonCode: null,
      primaryReasonPlain: 'No loan review on file',
      businessSummary: null,
      reviewReasonNotes: [],
      softFlagNotes: [],
      alsoNotedNotes: [],
      reviewQueue: null,
      wouldBeStp: null,
      stpKillSwitch: null,
      processingTimeMs: null,
      attention: [],
      gates: [],
      crossChecks: [],
      identityDedup: null,
      variations: [],
      pipelineStages: [],
      agent1: emptyAgents(),
      agent2: emptyAgents(),
      agent3: emptyAgents(),
      agent4: emptyAgents(),
      technical: [],
      errors: [],
      hasGatesSummary: false,
      hasPipelineSummary: false,
      services: [],
      serviceChips: [],
      orchestratorSummary: [],
      sourceCards: [],
      combined: null,
    };
  }

  const requestStatus = str(lr.requestStatus);
  const decision = str(lr.decision);
  const details = asRecord(lr.agentDetails) ?? {};
  const gatesSummary = asRecord(details.gatesSummary) ?? asRecord(details.gates_summary) ?? {};
  const pipeline =
    asRecord(details.pipelineSummary) ?? asRecord(details.pipeline_summary) ?? {};

  let outcomeLabel: LoanReviewOutcomeLabel = 'None';
  if (requestStatus === 'PENDING') outcomeLabel = 'Pending';
  else if (requestStatus === 'FAILED') outcomeLabel = 'Failed';
  else if (decision === 'APPROVE') outcomeLabel = 'Approved';
  else if (decision === 'REVIEW' || decision === 'DECLINE' || requestStatus === 'COMPLETED') {
    outcomeLabel = decision === 'APPROVE' ? 'Approved' : 'Needs review';
  }

  const primaryReasonCode =
    str(details.primaryReasonCode) ??
    str(details.primary_reason_code) ??
    str(asRecord(gatesSummary.decision)?.primary_reason_code) ??
    null;
  const combinedRaw = asRecord(gatesSummary.combined);
  const combined = withDerivedAdjustments(
    parseCombinedScore(combinedRaw),
    asRecord(pipeline.agent3),
  );
  const scoreCtx: PlainReasonScoreContext | null = combined
    ? { score: combined.score, band: combined.band }
    : null;
  const reviewQueueRaw =
    str(details.reviewQueue) ??
    str(details.review_queue) ??
    str(asRecord(gatesSummary.decision)?.review_queue) ??
    null;
  const reviewQueue = formatReviewQueue(alignReviewQueue(reviewQueueRaw, primaryReasonCode));
  const wouldBeStp =
    bool(details.wouldBeStp) ??
    bool(details.would_be_stp) ??
    bool(asRecord(gatesSummary.decision)?.would_be_stp);
  const stpKillSwitch =
    str(details.stpKillSwitch) ??
    str(details.stp_kill_switch) ??
    str(asRecord(gatesSummary.decision)?.stp_kill_switch);
  const processingTimeMs =
    num(details.processingTimeMs) ??
    num(details.processing_time_ms) ??
    num(pipeline.processingTimeMs);

  const gateRows = asArray(gatesSummary.gates_in_order).map((row) => {
    const g = asRecord(row) ?? {};
    const rawSubChecks = asArray(g.sub_checks).length
      ? asArray(g.sub_checks)
      : asArray(asRecord(g.detail)?.sub_checks);
    const subChecks: LoanReviewGateSubCheck[] = rawSubChecks.map((sc) => {
      const s = asRecord(sc) ?? {};
      const name = str(s.name) ?? '';
      return {
        name,
        label: SUB_CHECK_LABELS[name] ?? (name ? name.replace(/_/g, ' ') : 'Check'),
        status: str(s.status) ?? 'UNKNOWN',
        reasonCodes: asArray(s.reason_codes).map((c) => String(c)),
      };
    });
    return {
      gateId: str(g.gate_id) ?? 'UNKNOWN',
      title: str(g.title) ?? str(g.gate_id) ?? 'Gate',
      stage: str(g.stage) ?? '',
      status: str(g.status) ?? 'UNKNOWN',
      reasonCodes: asArray(g.reason_codes).map((c) => String(c)),
      blocksStp: Boolean(g.blocks_stp),
      subChecks,
    };
  });

  let identityDedup: LoanReviewIdentityDedup | null = null;
  const pairwise: LoanReviewCrossCheckRow[] = [];
  for (const row of asArray(gatesSummary.cross_checks)) {
    const c = asRecord(row) ?? {};
    const matchType = str(c.match_type) ?? '';
    const left = str(c.left);
    const right = str(c.right);
    const status = str(c.status) ?? '';
    const reasonCodes = asArray(c.reason_codes).map((x) => String(x));

    if (normalizeMatchType(matchType) === 'ID_DEDUP') {
      identityDedup = { status, reasonCodes };
      continue;
    }

    const sources = mapCrossCheckSources(matchType, left, right);
    if (!sources) continue;
    const cols = crossCheckColumnsFor(matchType);

    pairwise.push({
      matchType,
      compare: str(c.compare) ?? matchType,
      ...sources,
      asked: cols ? [cols.left, cols.right] : [],
      status,
      reasonCodes,
    });
  }

  const crossChecks = pivotCrossChecksToFieldRows(pairwise);

  const variations = asArray(gatesSummary.variations)
    .map((v) => {
      const r = asRecord(v) ?? {};
      const msg = str(r.message) ?? (asArray(r.reason_codes).map(String).join(', ') || null);
      return msg ? softVariationMessage(msg) : null;
    })
    .filter((x): x is string => Boolean(x));

  // Prefer field-level soft diffs (address/email/phone); drop misleading G02/G05 rollups.
  const fieldSoftVariations = variations.filter((v) => !isRedundantRollupVariationMessage(v));
  const attentionVariations =
    fieldSoftVariations.length > 0 ? fieldSoftVariations : variations;

  const attention: string[] = [];
  for (const g of gateRows) {
    if (['FAIL', 'REVIEW', 'UNAVAILABLE', 'ERROR'].includes(g.status)) {
      const why = g.reasonCodes[0] ? plainReason(g.reasonCodes[0], scoreCtx) : g.status;
      attention.push(`${g.title}: ${why}`);
    }
  }
  for (const v of attentionVariations.slice(0, 5)) attention.push(v);
  const primaryReasonPlainEarly = plainReason(primaryReasonCode, scoreCtx);
  if (primaryReasonCode && !attention.some((a) => a.includes(primaryReasonPlainEarly))) {
    attention.unshift(primaryReasonPlainEarly);
  }

  const a1raw = asRecord(pipeline.agent1);
  const a2raw = asRecord(pipeline.agent2);
  const a3raw = asRecord(pipeline.agent3);
  const a4raw = asRecord(pipeline.agent4);

  // Computed once and shared: Agent 4 renders every adjuster row, and Agent 2
  // needs its own row so its panel can state what 91/100 is actually worth on
  // the final score. Reading the same row in both places is what keeps the two
  // cards from disagreeing.
  const adjustmentRows = mapAdjustments(a4raw?.adjustments, combinedRaw);
  const historyAdjustment = adjustmentRows.find((r) => r.adjuster === 'history') ?? null;

  // Agent 1 is deterministic gates, not a model. It computes a score per domain,
  // and the panel used to render "Pass" over each one — the lossy version of a
  // number already in the payload. `preliminaryView` is gone from here too: it is
  // derived from the STP outcome, so it restated the answer shown in Agent 4's card.
  const agent1DomainScores = mapAgent1DomainScores(a1raw?.domainScores, combinedRaw);
  const agent1: LoanReviewAgentSlice = a1raw
    ? {
        present: true,
        blurb:
          'How the score above was built: each area adds up to a set number of points (its weight). Together they add up to the total.',
        lines: agent1DomainScores.length
          ? mapAgent1ContributionLines(agent1DomainScores, combinedRaw)
          : // No scored gates — an early failure, or a payload predating the score
            // fields. Status words are all there is, so keep the original
            // published-status-then-gate-row precedence rather than losing both.
            [
              { label: 'Identity', value: statusOrGate(a1raw.identityStatus, 'G01_IDENTITY', gateRows) },
              { label: 'Income', value: statusOrGate(a1raw.incomeStatus, 'G03_INCOME', gateRows) },
              { label: 'Employer', value: statusOrGate(a1raw.employerStatus, 'G04_EMPLOYMENT', gateRows) },
            ],
        score: num(a1raw.score) ?? (combinedRaw ? num(combinedRaw.agent1_score) : null),
        domainScores: agent1DomainScores,
      }
    : emptyAgents();

  // Agent 2 is this customer's own record with us, not a peer cohort. The card
  // must never let "nothing adverse found" and "we could not look" render the
  // same: `availability` is the only thing that separates them, and reading an
  // unavailable record as clean is the expensive mistake.
  const a2Availability = str(a2raw?.ownHistoryAvailability) ?? null;
  const a2Own = asRecord(a2raw?.ownHistory);
  const a2Events = Array.isArray(a2raw?.ownHistoryEvents)
    ? (a2raw.ownHistoryEvents as unknown[]).map(asRecord).filter(Boolean)
    : [];
  const a2Scored = a2Own !== null && a2Own !== undefined;
  const a2Findings = a2Events.filter((e) => e && !str(e.droppedReason));
  const a2Superseded = a2Events.filter((e) => e && str(e.droppedReason));

  const agent2: LoanReviewAgentSlice = a2raw
    ? {
        present: true,
        blurb: a2Scored
          ? "This customer's own past loans with us: what they borrowed and how they repaid."
          : "We could not read this customer's record with us. That is not the same as a clean record.",
        lines: [
          { label: 'Record', value: describeHistoryAvailability(a2Availability) },
          {
            label: 'Repayment score',
            value: a2Scored ? `${num(a2Own?.subscore) ?? '—'} / 100` : 'Not scored',
          },
          {
            // Counts the scored findings, which is also the number of headlines
            // the panel below renders — symptoms of the same problem are grouped
            // under one of them, so "3" here would not match what is on screen.
            label: 'Concerns found',
            value: a2Scored ? String(a2Findings.length) : '—',
          },
        ],
        summary: str(a2Own?.summary) ?? null,
        // Scored events first, then the superseded ones. Both are shown: an event
        // that was considered and deliberately not charged is information, and
        // hiding it makes the subscore look arbitrary.
        historyFindings: [...a2Findings, ...a2Superseded].map((e) => ({
          code: str(e!.code) ?? '',
          detail: str(e!.detail) ?? '',
          impact: str(e!.impact),
          deduction: num(e!.deduction) ?? 0,
          baseDeduction: num(e!.baseDeduction),
          recencyMultiplier: num(e!.recencyMultiplier),
          ageMonths: num(e!.ageMonths),
          supersededBy: supersededWinner(str(e!.droppedReason)),
        })),
        historyContext: a2Scored ? historyContextRows(a2Own) : [],
        historyScoreWorking: a2Scored
          ? {
              subscore: num(a2Own?.subscore) ?? 0,
              totalDeduction: num(a2Own?.totalDeduction) ?? 0,
            }
          : null,
        historyEffect: historyAdjustment,
      }
    : emptyAgents();

  const a3Findings = mapExternalFindings(a3raw?.events);
  const a3Subscore = mapExternalSubscore(a3raw?.externalSubscore);
  const agent3: LoanReviewAgentSlice = a3raw
    ? {
        present: true,
        lines: [
          { label: 'Macroeconomics risk', value: str(a3raw.macroRisk) ?? '—' },
          { label: 'Sector outlook', value: str(a3raw.sectorOutlook) ?? '—' },
          // Replaces the old `Confidence` row: that showed a model-generated number
          // produced before any finding was qualified, so it described nothing on
          // screen. This states what was actually researched.
          { label: 'Research', value: describeExternalCoverage(a3Subscore, a3Findings.length) },
        ],
        macroSummary: str(a3raw.macroSummary) ?? str(a3raw.macro_summary),
        sectorSummary: str(a3raw.sectorSummary) ?? str(a3raw.sector_summary),
        summary: str(a3raw.summary),
        // `riskFlags` deliberately not mapped — the backend no longer sends the old
        // free-text flags, and beside a scored finding they read as a duplicate.
        externalFindings: a3Findings,
        externalSubscore: a3Subscore,
      }
    : emptyAgents();

  const a4Business =
    str(a4raw?.businessSummary) ??
    str(a4raw?.summary) ??
    str(details.businessSummary) ??
    str(pipeline.businessSummary);

  const a4Outcome = str(a4raw?.outcome)?.toUpperCase() ?? '';
  const a4NeedsReviewFocus =
    a4Outcome === 'HUMAN_REVIEW' || a4Outcome === 'REVIEW';

  // Agent 4's card is where the two stages meet: Agent 1's evidence, each outside
  // signal as its own line, then the number the lane came from. Agent 4 does not
  // compute any of it — it writes the prose underneath.
  const agent4Adjustments = adjustmentRows;
  const agent4: LoanReviewAgentSlice = a4raw
    ? {
        present: true,
        lines: [
          { label: 'Outcome', value: str(a4raw.outcome) ?? '—' },
          ...(a4NeedsReviewFocus && str(a4raw.reviewFocus)
            ? [{ label: 'Review focus', value: str(a4raw.reviewFocus)! }]
            : []),
        ],
        summary: a4Business ?? str(a4raw.reasoningExcerpt),
        flags: asArray(a4raw.flags).map(String),
        score: num(a4raw.agent1Score) ?? (combinedRaw ? num(combinedRaw.agent1_score) : null),
        adjustments: agent4Adjustments,
        finalScore:
          num(a4raw.finalScore)
          ?? num(a4raw.compositeScore)
          ?? (combinedRaw ? num(combinedRaw.score) : null),
        keyPoints: asArray(a4raw.keyPoints).map(String).filter(Boolean).slice(0, 5),
        narrativeSource: str(a4raw.narrativeSource),
      }
    : emptyAgents();

  const rawBusinessSummary = resolveBusinessSummary({
    decision,
    primaryReasonCode,
    scoreCtx,
    published: [
      str(details.businessSummary),
      str(pipeline.businessSummary),
      str(a4raw?.businessSummary),
      a4Business,
    ],
  });
  const businessSummary = stripLegacySoftFlagParagraph(rawBusinessSummary);

  const publishedSoftNotes = [
    ...asArray(details.softFlagNotes),
    ...asArray(details.soft_flag_notes),
    ...asArray(pipeline.softFlagNotes),
    ...asArray(pipeline.soft_flag_notes),
  ]
    .map(String)
    .map((s) => s.trim())
    .filter(Boolean);

  const publishedReviewNotes = [
    ...asArray(details.reviewReasonNotes),
    ...asArray(details.review_reason_notes),
    ...asArray(pipeline.reviewReasonNotes),
    ...asArray(pipeline.review_reason_notes),
  ]
    .map(String)
    .map((s) => s.trim())
    .map((s) => enrichAgent1ScoreNote(s, scoreCtx))
    .filter(Boolean);

  const publishedAlsoNoted = [
    ...asArray(details.alsoNotedNotes),
    ...asArray(details.also_noted_notes),
    ...asArray(pipeline.alsoNotedNotes),
    ...asArray(pipeline.also_noted_notes),
  ]
    .map(String)
    .map((s) => s.trim())
    .filter(Boolean);

  const softFromCodes = new Set<string>();
  // Soft catalog codes from any gate status (incl. G05 REVIEW rollups) + cross-checks.
  for (const g of gateRows) {
    for (const code of g.reasonCodes) {
      const note = SOFT_FLAG_NOTE[code];
      if (note) softFromCodes.add(note);
    }
  }
  for (const row of crossChecks) {
    if ((row.status || '').toUpperCase() !== 'PASS_WITH_VARIATION') continue;
    for (const code of row.reasonCodes ?? []) {
      const note = SOFT_FLAG_NOTE[String(code)];
      if (note) softFromCodes.add(note);
    }
  }

  const isApprove = decision === 'APPROVE';
  const isReview = decision === 'REVIEW' || decision === 'DECLINE';
  const primaryIsNameHardStop = Boolean(
    primaryReasonCode && NAME_HARD_STOP_CODES.has(primaryReasonCode.toUpperCase()),
  );

  const softFlagNoteTexts = (isApprove || isReview)
    ? (publishedSoftNotes.length > 0
        ? [...new Set(publishedSoftNotes)].slice(0, 6)
        : softFromCodes.size > 0
          ? [...new Set(softFromCodes)].slice(0, 6)
          : isApprove
            ? attentionVariations.slice(0, 6)
            : fieldSoftVariations.slice(0, 6))
    : [];

  let reviewReasonNoteTexts: string[] = [];
  let alsoNotedNoteTexts: string[] = [];
  if (isReview) {
    const published = [...new Set(publishedReviewNotes)]
      .filter((n) => !looksLikeSoftDifferenceNote(n))
      .slice(0, 8);
    if (published.length > 0) {
      if (primaryIsNameHardStop) {
        reviewReasonNoteTexts = published.filter((n) => !looksLikeEmployerReviewNote(n));
        alsoNotedNoteTexts = [
          ...publishedAlsoNoted,
          ...published.filter((n) => looksLikeEmployerReviewNote(n)),
        ];
      } else {
        reviewReasonNoteTexts = published;
        alsoNotedNoteTexts = [...publishedAlsoNoted];
      }
    } else {
      reviewReasonNoteTexts = buildReviewReasonNotes({ gateRows, primaryReasonCode, scoreCtx });
      alsoNotedNoteTexts = [...publishedAlsoNoted];
    }
    alsoNotedNoteTexts = [...new Set(alsoNotedNoteTexts)].slice(0, 6);
  } else if (isApprove) {
    // Score-driven Approve can still leave employer (etc.) in REVIEW — surface as also-noted.
    alsoNotedNoteTexts =
      publishedAlsoNoted.length > 0
        ? [...new Set(publishedAlsoNoted)].filter((n) => !looksLikeSoftDifferenceNote(n)).slice(0, 6)
        : buildAlsoNotedNotesForApprove({ gateRows, scoreCtx });
  }

  const noteRefs = { ...parseNoteRefs(pipeline.noteRefs), ...parseNoteRefs(details.noteRefs) };
  const softFlagNotes = enrichReasonNotes(softFlagNoteTexts, crossChecks, noteRefs);
  const reviewReasonNotes = enrichReasonNotes(reviewReasonNoteTexts, crossChecks, noteRefs);
  const alsoNotedNotes = enrichReasonNotes(alsoNotedNoteTexts, crossChecks, noteRefs);
  const businessSummaryForHero = isApprove
    ? softenApproveSummaryForAlsoNoted(businessSummary, alsoNotedNotes.length)
    : businessSummary;

  // Hero already lists blocking reasons + soft diffs — Attention is redundant.
  const heroCoversIssues =
    Boolean(businessSummaryForHero) &&
    (reviewReasonNotes.length > 0 ||
      softFlagNotes.length > 0 ||
      alsoNotedNotes.length > 0 ||
      isApprove);

  const blockingGates = gateRows.filter((g) => g.blocksStp);
  const softGates = gateRows.filter((g) => g.status === 'PASS_WITH_VARIATION');
  const gatesStage = stageFromAgent(
    gateRows.length > 0,
    softGates.length > 0 && blockingGates.length === 0,
    blockingGates.length > 0,
    blockingGates[0]
      ? `${blockingGates[0].title}`
      : softGates[0]
        ? 'Soft flags only'
        : gateRows.length
          ? 'All clear'
          : 'No gate summary',
  );

  const a1Stage = stageFromAgent(
    agent1.present,
    (agent1.concerns?.length ?? 0) > 0,
    (agent1.concerns ?? []).some((c) => c.includes('failed')),
    agent1.present ? String(agent1.lines[0]?.value ?? 'Agent 1') : 'Not on this run',
  );
  const a2Stage = stageFromAgent(
    agent2.present,
    false,
    false,
    agent2.present ? `${agent2.lines[0]?.value ?? '—'} similar` : 'Not on this run',
  );
  const a3Stage = stageFromAgent(
    agent3.present,
    (agent3.flags?.length ?? 0) > 0,
    (agent3.flags ?? []).some((f) => f.includes('failed')),
    agent3.present ? String(agent3.lines[0]?.value ?? 'Agent 3') : 'Not on this run',
  );
  const a4Blocked =
    agent4.present &&
    ['HUMAN_REVIEW', 'REVIEW'].includes(String(agent4.lines[0]?.value ?? '').toUpperCase());
  const a4Stage = stageFromAgent(
    agent4.present,
    false,
    a4Blocked,
    agent4.present ? String(agent4.lines[0]?.value ?? 'Agent 4') : 'Not on this run',
  );

  const pipelineStages: LoanReviewPipelineStage[] = [
    { id: 'gates', label: 'Gates', ...gatesStage },
    { id: 'agent1', label: 'Preliminary view (A1)', ...a1Stage },
    { id: 'agent2', label: 'History (A2)', ...a2Stage },
    { id: 'agent3', label: 'External (A3)', ...a3Stage },
    { id: 'agent4', label: 'Decision (A4)', ...a4Stage },
  ];

  const errors = [
    ...asArray(details.errors).map(String),
    ...asArray(pipeline.errors).map(String),
  ].filter(Boolean);

  const technical: Array<{ label: string; value: string }> = [
    { label: 'Correlation ID', value: str(details.correlationId) ?? str(details.correlation_id) ?? '—' },
    { label: 'Trace ID', value: str(details.traceId) ?? str(details.trace_id) ?? '—' },
    { label: 'Policy', value: `${str(details.policyId) ?? '—'} / ${str(details.policyVersion) ?? '—'}` },
    { label: 'Evaluator', value: str(details.evaluatorVersion) ?? '—' },
    {
      label: 'Processing',
      value: processingTimeMs != null ? `${processingTimeMs} ms` : '—',
    },
    ...(wouldBeStp != null
      ? [{ label: 'Would be STP', value: wouldBeStp ? 'yes' : 'no' }]
      : []),
    ...(stpKillSwitch
      ? [{ label: 'Kill switch', value: stpKillSwitch }]
      : []),
  ];

  const sourceCards = asArray(gatesSummary.source_cards)
    .map(parseSourceCard)
    .filter((c): c is SourceCardViewModel => Boolean(c));

  // Score lane can refine REJECT vs REVIEW, but must not override a bus REVIEW
  // with Approved — G08 (visitor/device) can block STP while the score stays APPROVE.
  const busNeedsHuman =
    decision === 'REVIEW' ||
    decision === 'DECLINE' ||
    decision === 'HUMAN_REVIEW';
  if (combined?.lane) {
    const fromLane = LANE_OUTCOME_LABEL[combined.lane.toUpperCase()];
    if (fromLane) {
      if (busNeedsHuman && fromLane === 'Approved') {
        outcomeLabel = 'Needs review';
      } else if (!busNeedsHuman || fromLane !== 'Approved') {
        outcomeLabel = fromLane;
      }
    }
  }
  // Gate outcome / visitor STP block are further signals when the score lane said Approve.
  const gateOutcome = (
    str(details.gateOutcome) ??
    str(details.gate_outcome) ??
    str(asRecord(gatesSummary.decision)?.application_outcome) ??
    ''
  ).toUpperCase();
  const visitorOrDevicePrimary = Boolean(
    primaryReasonCode &&
      (/^VISITOR_/.test(primaryReasonCode.toUpperCase()) ||
        /^DEVICE_RISK_/.test(primaryReasonCode.toUpperCase())),
  );
  if (
    outcomeLabel === 'Approved' &&
    (busNeedsHuman ||
      gateOutcome === 'MANUAL_REVIEW_REQUIRED' ||
      (wouldBeStp === false && visitorOrDevicePrimary))
  ) {
    outcomeLabel = 'Needs review';
  }

  const services = buildServiceSections(gateRows, crossChecks, agent1, sourceCards);
  const orchestratorSummary = buildOrchestratorSummary({
    gateRows,
    agentsPresent: [agent1.present, agent2.present, agent3.present, agent4.present],
    processingTimeMs,
    wouldBeStp,
    stpKillSwitch,
  });

  return {
    outcomeLabel,
    requestStatus,
    decision,
    primaryReasonCode,
    primaryReasonPlain: plainReason(primaryReasonCode, scoreCtx),
    businessSummary: businessSummaryForHero,
    reviewReasonNotes,
    softFlagNotes,
    alsoNotedNotes,
    reviewQueue,
    wouldBeStp,
    stpKillSwitch,
    processingTimeMs,
    attention: heroCoversIssues ? [] : attention.slice(0, 12),
    gates: gateRows,
    crossChecks,
    identityDedup,
    variations,
    pipelineStages,
    agent1,
    agent2,
    agent3,
    agent4,
    technical,
    errors,
    hasGatesSummary: gateRows.length > 0,
    hasPipelineSummary: Boolean(a1raw || a2raw || a3raw || a4raw),
    services,
    serviceChips: buildServiceChips(sourceCards, services),
    orchestratorSummary,
    sourceCards,
    combined,
  };
}
